import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, Square, MapPin, Activity, Volume2, Volume1, Lock, Unlock, Maximize2, Minimize2, Bug, RotateCcw, Flag } from 'lucide-react';
import { toast } from 'sonner';
import TopNav from '@/components/TopNav';
import ZoneIndicator from '@/components/ZoneIndicator';
import ClickableMetric from '@/components/ClickableMetric';
import ForceBalanceIndicator from '@/components/ForceBalanceIndicator';
import StrokeVisualizer from '@/components/StrokeVisualizer';
import FirmwareCrosscheckCard from '@/components/FirmwareCrosscheckCard';
import OarlockDebugPanel from '@/components/OarlockDebugPanel';
import SessionCsvLogger from '@/components/SessionCsvLogger';
import CockpitDisplay from '@/components/CockpitDisplay';
import { useTheme } from '@/contexts/ThemeContext';
import { calculateHRZone, maxHrInUse, restHrInUse, HR_ZONE_METHOD } from '@/lib/hrZones';
import { generateTcxFile } from '@/lib/tcxExport';
import { computeHrZoneDistribution, buildHrZoneSeries, averageSeries } from '@/lib/hrZones';
import { formatRhythmRatio } from '@/lib/rhythmRatio';
import { useWakeLock } from '@/hooks/useDeviceFeatures';
import { useGPS } from '@/hooks/useGPS';
import useWBTTarget from '@/hooks/useWBTTarget';
import { useSharedStrokeDetection } from '@/contexts/StrokeDetectionContext';
import useCurveSnapshots from '@/hooks/useCurveSnapshots';
import useStrokeSplit from '@/hooks/useStrokeSplit';
import usePieces from '@/hooks/usePieces';
import { SwipeScreens, ScreenBadge, PieceScreen, PieceSetupDialog } from '@/components/PiecesUI';
import { RowingDetectedLight } from '@/components/RowingDetectedLight';
import { SessionLogButton } from '@/components/SessionLogButton';
import { setLogPowerSample } from '@/lib/strokeSensorLogger';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { speedToSplit, formatSplitTenths, MIN_SPLIT_SPEED } from '@/lib/strokeSplit';
import { useAudioNotifications } from '@/hooks/useAudioNotifications';
import { useRaceMode, useRaceCues, isCoxVoiceEnabled } from '@/hooks/useRaceMode';
import useRaceSplits from '@/hooks/useRaceSplits';
import RaceSplitsCard from '@/components/RaceSplitsCard';
import { saveWorkoutResilient, startOnlineAutoSync, getOutboxSize } from '@/services/offlineSync';
import { getLastPaired, isLastPairedToastDismissed, markLastPairedToastDismissed } from '@/services/oarlock/pairingMemory';
import { getPM5Connection } from '@/lib/pm5Connection';
import usePm5StrokeWatts from '@/hooks/usePm5StrokeWatts'; import useCheckFactorFeed from '@/hooks/useCheckFactorFeed'; import { computeCheckFactor } from '@/lib/checkFactor';
import { MAX_WORKOUT_DURATION_SECONDS } from '@/contexts/WorkoutContext';
import { useWorkout } from '@/contexts/WorkoutContext';
import useOarlockCalibration from '@/hooks/useOarlockCalibration';
import { useAppMode, useMetricPreferences, MODE_LABELS } from '@/hooks/useAppMode';
import {
  calculateBoatMass,
  calculatePowerFromSpeed,
  calculateAverageSplit,
  calculateDistancePerStroke,
  calculateWorkPerStroke,
  estimateStrokeLength,
  CREW_COUNTS
} from '@/lib/boatCalculations';
import { keytelKcalPerMinute, ageFromDob } from '@/lib/keytelCalories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WorkoutMonitorV2() {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  
  // GPS hook - auto-starts with phone fallback
  const gps = useGPS();
  
  // Stroke detection now declared below (after WorkoutContext destructure)
  // so we can pass mode-aware signals into it.
  
  // Audio notifications for volume control
  const audio = useAudioNotifications();
  
  // Orientation detection
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    
    const handleOrientationChange = () => {
      // Small delay to let the browser update dimensions
      setTimeout(handleResize, 100);
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Also listen to screen orientation API if available
    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleOrientationChange);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleOrientationChange);
      }
    };
  }, []);
  
  const [user, setUser] = useState(null);

  // ── Workout state lives in WorkoutContext (survives tab navigation) ───────
  const {
    workoutActive, setWorkoutActive,
    workoutPaused, setWorkoutPaused,
    elapsedTime, setElapsedTime,
    pm5ElapsedTime,
    movingTime,
    distance, setDistance,
    strokes, setStrokes,
    calories, setCalories,
    targetDistance, setTargetDistance,
    currentSplit, setCurrentSplit,
    strokeRate, setStrokeRate,
    heartRate, setHeartRate,
    watts, setWatts,
    metersPerSecond, setMetersPerSecond,
    smoothedSpeed,
    rhythmRatio,
    pm5Device, setPm5Device,
    crewzConnected,
    workoutStartTimeRef,
    workoutDataRef,
    startWorkout: ctxStartWorkout,
    stopWorkout: ctxStopWorkout,
    resetWorkout,
    livePowerRef,
  } = useWorkout();

  // workoutStartTime alias used throughout this file
  const workoutStartTime = workoutStartTimeRef;

  // Stroke detection from accelerometer or GPS speed patterns.
  // Mode-aware: Mode 2 (phone-only) uses autocorrelation only; Mode 1/3 use
  // the hybrid blend (overridden when CrewZ IMU streams via 'crewz-imu-update').
  // Shared app-wide instance (StrokeDetectionProvider): keeps running on every tab.
  const strokeDetection = useSharedStrokeDetection();

  const [avgSplit3, setAvgSplit3] = useState(0); // Average split over last 3 strokes
  const [workoutName, setWorkoutName] = useState(null); // Session name for saving
  const [countdown, setCountdown] = useState(null); // null = no countdown, number = seconds remaining
  const [countdownFade, setCountdownFade] = useState(false); // For fade animation
  const [hrDevice, setHrDevice] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  // Demo Boost — scales the demo simulator's base speed up ~1.4× so the
  // rolling average crosses the WBT for high-end boat classes (M8+, M4x
  // etc). Only meaningful in demo mode; ignored when a real PM5 is
  // connected. Persisted in localStorage under `demo_boost`.
  const [demoBoost, setDemoBoost] = useState(false);
  const [workoutCompleted, setWorkoutCompleted] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [coxModeEnabled, setCoxModeEnabled] = useState(false);
  const [screenLocked, setScreenLocked] = useState(false); // Screen lock to prevent accidental touches
  const [stopHoldProgress, setStopHoldProgress] = useState(0); // Progress for long-press stop (0-100)

  // ── Pace Target overlay state ─────────────────────────────────────────────
  // Display-only coaching cue. Toggling these MUST NOT touch the underlying
  // session recording (per product brief). Persisted in localStorage so the
  // rower's last target survives a page reload mid-session.
  //
  // Future: when a Program is loaded on the Monitor, `inProgram` will flip
  // true and the program step's target wins — the manual control is hidden
  // entirely in that case.
  const [paceTargetOn, setPaceTargetOn] = useState(() => {
    try { return localStorage.getItem('crewz_pace_target_on') === '1'; } catch { return false; }
  });
  const [paceTargetSplit, setPaceTargetSplit] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('crewz_pace_target_split') || '115', 10);
      return Number.isFinite(v) && v >= 60 && v <= 600 ? v : 115;
    } catch { return 115; }
  });
  useEffect(() => {
    try { localStorage.setItem('crewz_pace_target_on', paceTargetOn ? '1' : '0'); } catch { /* noop */ }
  }, [paceTargetOn]);
  useEffect(() => {
    try { localStorage.setItem('crewz_pace_target_split', String(paceTargetSplit)); } catch { /* noop */ }
  }, [paceTargetSplit]);

  // ── Cockpit tile font-size control ────────────────────────────────────────
  // Lets the rower enlarge the numbers in the three top tiles + the two side
  // tiles (not the hero/main split). Step 0 = default; each A+ press bumps one
  // step up to a hard cap of 4 so the layout never breaks. Persisted so the
  // chosen size survives a reload.
  const TILE_FONT_STEP_MAX = 4;
  const [tileFontStep, setTileFontStep] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('crewz_tile_font_step') || '0', 10);
      return Number.isFinite(v) ? Math.max(0, Math.min(TILE_FONT_STEP_MAX, v)) : 0;
    } catch { return 0; }
  });
  useEffect(() => {
    try { localStorage.setItem('crewz_tile_font_step', String(tileFontStep)); } catch { /* noop */ }
  }, [tileFontStep]);
  const bumpTileFont = (delta) => setTileFontStep(s => Math.max(0, Math.min(TILE_FONT_STEP_MAX, s + delta)));

  // ── Fullscreen + Debug Panel state ────────────────────────────────────────
  // Fullscreen uses the standard Web Fullscreen API. State stays in sync with
  // the browser via fullscreenchange so OS-level exits (e.g. swipe down on
  // Android) update the icon.
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [nkDebugOpen, setNkDebugOpen] = useState(() => {
    try { return localStorage.getItem('crewz_nk_debug_panel') === '1'; } catch { return false; }
  });
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // ── Oarlock heartbeat (Mode 5 — XBoat XPod) ──────────────────────────────
  // Track time-since-last-frame so the on-screen MODE pill can pulse green
  // (data flowing) or amber (paired but silent). Polled at 1Hz so the badge
  // re-evaluates freshness without re-rendering on every BLE notification.
  // Listens to the generic `crewz-oarlock-debug-frame` (and legacy NK event
  // for backwards compat).
  const nkLastFrameRef = useRef(0);
  const [nkFreshness, setNkFreshness] = useState('idle'); // 'idle' | 'live' | 'stale'
  useEffect(() => {
    const onFrame = () => { nkLastFrameRef.current = Date.now(); };
    window.addEventListener('crewz-oarlock-debug-frame', onFrame);
    window.addEventListener('crewz-nk-debug-frame', onFrame);
    const tick = setInterval(() => {
      const last = nkLastFrameRef.current;
      if (!last) { setNkFreshness('idle'); return; }
      const age = Date.now() - last;
      setNkFreshness(age < 2000 ? 'live' : age < 8000 ? 'stale' : 'idle');
    }, 1000);
    return () => {
      window.removeEventListener('crewz-oarlock-debug-frame', onFrame);
      window.removeEventListener('crewz-nk-debug-frame', onFrame);
      clearInterval(tick);
    };
  }, []);
  const toggleFullscreen = async (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      toast.error('Fullscreen not supported on this browser');
    }
  };
  const toggleNkDebug = (e) => {
    if (e) { e.stopPropagation(); }
    setNkDebugOpen(v => {
      const next = !v;
      try { localStorage.setItem('crewz_nk_debug_panel', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };
  const stopHoldTimerRef = useRef(null);

  // ── Race mode (under Target picker) ─────────────────────────────────────
  // Standard (default): traditional 10s countdown then start.
  // Race: enters READY, motion-detect launch (boat lurches → start, no countdown).
  // Voice cues fire every 500m + a "250 to go, empty the tank!" cue.
  const [targetMode, setTargetMode] = useState('standard'); // 'standard' | 'race' | 'time'
  // Time-based target — completely independent of the Session Goal
  // duration (which lives in `sessionGoal.durationMin` and only powers
  // the left-tile ring widget). Selecting a target here MUST NOT alter
  // the ring's setup, per product brief.
  const [targetDurationMin, setTargetDurationMin] = useState(30);
  const race = useRaceMode();
  useRaceCues({ active: workoutActive && targetMode === 'race', distance, target: targetDistance });

  // ── Race splits (post-race summary) ─────────────────────────────────────
  // Captures every 500m boundary crossing during a race. Splits live in state
  // so we can show the modal immediately and persist them with the workout.
  const { splits: raceSplits, reset: resetRaceSplits } = useRaceSplits({
    active: workoutActive && targetMode === 'race',
    distance,
    elapsedSeconds: elapsedTime,
    target: targetDistance,
  });
  const [showRaceSplits, setShowRaceSplits] = useState(false);

  // ── Offline-first sync: replay any queued workouts as soon as we come back online.
  useEffect(() => {
    const unsub = startOnlineAutoSync(async (p) => {
      return fetch(`${API}/workouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(p),
      });
    });
    // Surface the outbox state on mount so the rower knows
    const pending = getOutboxSize();
    if (pending > 0) {
      toast.info(`Syncing ${pending} offline workout${pending === 1 ? '' : 's'}…`);
    }
    return unsub;
  }, []);

  // ── Last-paired toast — one-tap reconnect prompt (replaces the Settings detour). ──
  useEffect(() => {
    if (workoutActive) return undefined;
    const last = getLastPaired();
    if (!last) return undefined;
    if (isLastPairedToastDismissed()) return undefined;
    // Delay so it doesn't fight with first-render churn
    const t = setTimeout(() => {
      toast(`Previously paired ${last.display_name || last.vendor} — use last device?`, {
        id: 'last-paired-toast',
        duration: 12000,
        action: {
          label: 'Reconnect',
          onClick: () => {
            markLastPairedToastDismissed();
            // BLE reconnect blocked on GATT UUIDs — for now route to Settings.
            // When drivers ship, replace with `OarlockManager.reconnect(last.vendor)`.
            navigate('/settings');
          },
        },
        cancel: {
          label: 'Dismiss',
          onClick: () => markLastPairedToastDismissed(),
        },
      });
    }, 1500);
    return () => clearTimeout(t);
  }, [workoutActive, navigate]);

  
  // New metrics state
  const [peakPower, setPeakPower] = useState(0);
  const [checkFactorNumeric, setCheckFactorNumeric] = useState(0);
  const [boatClass, setBoatClass] = useState(() => {
    try { return localStorage.getItem('default_boat_type') || '1x'; } catch { return '1x'; }
  });
  const [weightCategory, setWeightCategory] = useState('men');
  const [isCoxswain, setIsCoxswain] = useState(false);
  const [crewWeightMode, setCrewWeightMode] = useState('category');
  const [crewAvgWeight, setCrewAvgWeight] = useState(null);
  
  const demoHrInterval = useRef(null);
  const hideControlsTimeout = useRef(null);
  const strokeHistoryRef = useRef([]); // Track last 2 strokes for M/S smoothing
  const checkFactorDataRef = useRef([]); // Track speed data for check factor graph
  const splitHistoryRef = useRef({ splits: [], lastPhase: 0 }); // Track last splits for averaging
  const speedHistoryRef = useRef([]); // Track speed for check factor calculation
  const smoothedSplitRef = useRef([]); // Track last 2 split readings for smoothing
  
  // State for check factor data (for re-renders)
  const [checkFactorData, setCheckFactorData] = useState([]);

  // Check Factor panel visibility — user preference toggled from Settings.
  // Default ON per product spec. Persist to localStorage AND backend via
  // the User Preferences endpoint (see DeviceConnection.js).
  const [showCheckFactor, setShowCheckFactor] = useState(() => {
    try {
      const raw = localStorage.getItem('show_check_factor');
      // Default OFF for new users — they opt in via Settings. Once they've
      // toggled it, whatever they chose persists (stored as 'true'/'false').
      return raw === 'true';
    } catch { return false; }
  });
  // Listen for cross-tab (and same-tab, dispatched manually) storage changes
  // so flipping the toggle in Settings updates the Monitor live.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'show_check_factor') {
        setShowCheckFactor(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('crewz:show_check_factor_changed', (e) => {
      setShowCheckFactor(!!e.detail);
    });
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // ── Left flanking tile — universal slot for CF% / Session Goal / off ──
  // Reads the `left_tile_display` preference from localStorage (mirrored
  // to the backend on write via DeviceConnection). Values: "off" |
  // "check_factor" | "session_goal". Mode 3 (Ergo/PM5) forces
  // "session_goal" because CF% is GPS-derived.
  const [leftTilePref, setLeftTilePref] = useState(() => {
    try {
      const raw = localStorage.getItem('left_tile_display');
      if (raw === 'off' || raw === 'check_factor' || raw === 'session_goal') return raw;
      // Fall back to the legacy show_check_factor flag so existing users
      // don't have to reconfigure.
      const legacy = localStorage.getItem('show_check_factor');
      return legacy === 'true' ? 'check_factor' : 'off';
    } catch { return 'off'; }
  });
  useEffect(() => {
    const onChange = (e) => setLeftTilePref(e.detail || 'off');
    window.addEventListener('crewz:left_tile_display_changed', onChange);
    return () => window.removeEventListener('crewz:left_tile_display_changed', onChange);
  }, []);

  // ── Session Goal state — user-set duration + target that the pie
  //     widget evaluates minute by minute. Kept locally on the Monitor
  //     since it's only useful while the user is actively rowing.
  const [sessionGoal, setSessionGoal] = useState(() => {
    try {
      const raw = localStorage.getItem('session_goal');
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return { durationMin: 30, targetType: 'split', targetValue: 130 };
  });
  const updateSessionGoal = (partial) => {
    setSessionGoal((prev) => {
      const next = { ...prev, ...partial };
      try { localStorage.setItem('session_goal', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  // Called by the Session Goal pie when the final wedge closes. Triggers
  // the standard end-workout flow so the row lands in History with a
  // `goal_summary` snapshot attached.
  const handleSessionGoalComplete = ({ minutes }) => {
    try {
      const passed = minutes.filter((v) => v === true).length;
      const total = minutes.length;
      window.dispatchEvent(new CustomEvent('crewz:goal_complete', {
        detail: { passed, total, minutes, goal: sessionGoal },
      }));
    } catch { /* noop */ }
    // Defer to next tick so the pie's final wedge render commits before
    // the workout tears down.
    window.setTimeout(() => {
      if (typeof stopWorkout === 'function') stopWorkout();
    }, 200);
  };

  // Wake Lock - prevents screen from sleeping during workouts
  useWakeLock();
  
  // Background operation timing is handled inside WorkoutContext now.
  // Keep refs here in case other local logic needs them later.
  const lastVisibleTimeRef = useRef(Date.now());
  const backgroundElapsedRef = useRef(0);
  
  // Smooth split over a 3-second rolling window to suppress GPS jitter (Brief C).
  // This affects ONLY the displayed/saved split — raw speed and distance are
  // left untouched so cumulative metrics remain accurate.
  const SPLIT_SMOOTHING_WINDOW_MS = 3000;
  const updateSmoothedSplit = (newSplit) => {
    if (newSplit <= 0 || newSplit > 600) return; // Ignore invalid splits (0 or > 10 min/500m)

    const now = Date.now();
    smoothedSplitRef.current.push({ value: newSplit, t: now });

    // Drop samples older than the smoothing window
    const cutoff = now - SPLIT_SMOOTHING_WINDOW_MS;
    while (smoothedSplitRef.current.length > 0 && smoothedSplitRef.current[0].t < cutoff) {
      smoothedSplitRef.current.shift();
    }

    if (smoothedSplitRef.current.length > 0) {
      const sum = smoothedSplitRef.current.reduce((acc, s) => acc + s.value, 0);
      const avgSplit = sum / smoothedSplitRef.current.length;
      setCurrentSplit(avgSplit);
    }
  };

  // Load Cox mode setting
  useEffect(() => {
    const savedCoxMode = localStorage.getItem('cox_mode_enabled');
    if (savedCoxMode === 'true') {
      setCoxModeEnabled(true);
    }
  }, []);

  // Calculate smoothed meters per second (averaged over 2 strokes)
  const calculateSmoothedMPS = (currentDistance, currentTime) => {
    const now = Date.now();
    
    // Add current stroke data
    strokeHistoryRef.current.push({
      distance: currentDistance,
      time: currentTime,
      timestamp: now
    });
    
    // Keep only last 2 strokes
    if (strokeHistoryRef.current.length > 2) {
      strokeHistoryRef.current.shift();
    }
    
    // Need at least 2 data points to calculate
    if (strokeHistoryRef.current.length >= 2) {
      const oldest = strokeHistoryRef.current[0];
      const newest = strokeHistoryRef.current[strokeHistoryRef.current.length - 1];
      
      const distanceDiff = newest.distance - oldest.distance;
      const timeDiff = (newest.timestamp - oldest.timestamp) / 1000; // Convert to seconds
      
      if (timeDiff > 0) {
        const mps = distanceDiff / timeDiff;
        return Math.max(0, mps); // Ensure non-negative
      }
    }
    
    return 0;
  };

  // Auto-hide controls after 10 seconds during active workout (disabled in Cox mode)
  const resetHideTimer = () => {
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    setControlsVisible(true);
    
    // In Cox mode, controls never auto-hide
    if (coxModeEnabled) {
      return;
    }
    
    if (workoutActive && !workoutPaused) {
      hideControlsTimeout.current = setTimeout(() => {
        setControlsVisible(false);
      }, 10000); // 10 seconds
    }
  };

  // Handle screen touch/click to show controls
  const handleScreenTouch = () => {
    if (workoutActive && !controlsVisible) {
      resetHideTimer();
    }
  };

  // Start hide timer when workout becomes active
  useEffect(() => {
    // In Cox mode, always keep controls visible
    if (coxModeEnabled) {
      setControlsVisible(true);
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
      return;
    }
    
    if (workoutActive && !workoutPaused) {
      resetHideTimer();
    } else {
      // Always show controls when paused or not active
      setControlsVisible(true);
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
    }
    
    return () => {
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
    };
  }, [workoutActive, workoutPaused]);

  useEffect(() => {
    fetch(`${API}/user/profile`, { credentials: 'include' })
      .then(res => res.json())
      .then(userData => {
        setUser(userData);
        // Load weight category from profile
        if (userData.weight_category) {
          setWeightCategory(userData.weight_category);
        }
        // Load coxswain settings from profile
        if (userData.is_coxswain !== undefined) {
          setIsCoxswain(userData.is_coxswain);
        }
        if (userData.crew_weight_mode) {
          setCrewWeightMode(userData.crew_weight_mode);
        }
        if (userData.crew_avg_weight) {
          setCrewAvgWeight(userData.crew_avg_weight);
        }
      })
      .catch(err => console.error('Failed to load user:', err));

    // Load boat class from localStorage
    const savedBoatClass = localStorage.getItem('default_boat_type');
    if (savedBoatClass) {
      setBoatClass(savedBoatClass);
    }

    // NOTE: PM5 and HR device states are not loaded from localStorage on startup.
    // They default to disconnected and require manual connection from Settings page.
    // The global window.pm5Connection object is the source of truth for PM5 connection.
    // PM5 + HR window event listeners live in WorkoutContext so they survive
    // tab navigation. This effect only seeds local one-shot setup.

    // Check if PM5 is already connected (e.g., from boat selection or settings)
    if (window.pm5Connection && window.pm5Connection.isConnected) {
      const deviceName = window.pm5Connection.device?.name || 'PM5';
      setPm5Device(deviceName);
      console.log('🚣 PM5 already connected:', deviceName);
    }

    // Check demo mode from localStorage
    const savedDemoMode = localStorage.getItem('demo_mode');
    if (savedDemoMode === 'true') {
      setDemoMode(true);
    } else {
      setDemoMode(false);
    }
    // Demo Boost mirrors the demoMode read pattern.
    setDemoBoost(localStorage.getItem('demo_boost') === 'true');
  }, []);

  // ── Live demo-boost sync (mirrors demoMode's cross-tab listener) ──────
  useEffect(() => {
    const handleCustom = (e) => setDemoBoost(!!e.detail);
    const handleStorage = (e) => {
      if (e.key === 'demo_boost') setDemoBoost(e.newValue === 'true');
    };
    const handleFocus = () => {
      setDemoBoost(localStorage.getItem('demo_boost') === 'true');
    };
    window.addEventListener('crewz-demo-boost-changed', handleCustom);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('crewz-demo-boost-changed', handleCustom);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // ── Live demo-mode sync ────────────────────────────────────────────────
  // Without this, toggling demo OFF on the DeviceConnection page while the
  // Monitor is already mounted (back-stack route) leaves the demo
  // simulation running — the original mount-only useEffect doesn't re-read
  // localStorage. We listen for both the in-tab custom event we dispatch
  // ourselves AND the cross-tab `storage` event for completeness.
  useEffect(() => {
    const handleCustom = (e) => setDemoMode(!!e.detail);
    const handleStorage = (e) => {
      if (e.key === 'demo_mode') setDemoMode(e.newValue === 'true');
    };
    const handleFocus = () => {
      setDemoMode(localStorage.getItem('demo_mode') === 'true');
    };
    window.addEventListener('crewz-demo-mode-changed', handleCustom);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('crewz-demo-mode-changed', handleCustom);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);
  
  // When PM5 connects, stop phone-based services (GPS and stroke detection)
  useEffect(() => {
    if (pm5Device) {
      // Stop GPS tracking - PM5 provides distance directly
      if (gps.isTracking) {
        console.log('🚣 PM5 connected - stopping phone GPS');
        gps.stopTracking();
      }
    }
  }, [pm5Device, gps]);

  // Auto-start GPS when workout starts (only if no PM5 connected - ERG mode doesn't need GPS)
  // NOTE: We use phone GPS by default to avoid triggering Web Serial API prompts.
  // External GPS must be connected manually from the Settings page before starting a workout.
  useEffect(() => {
    if (workoutActive && !workoutPaused && !pm5Device && !gps.isTracking) {
      const preference = localStorage.getItem('gps_preference') || 'auto';
      
      // For 'auto' mode: Use phone GPS directly (external GPS should be pre-connected from Settings)
      // For 'phone' mode: Use phone GPS
      // For 'external' mode: Only use external if already connected, otherwise fallback to phone
      if (preference === 'external' && gps.isExternalGPS) {
        // External GPS is already connected (from Settings), just mark as tracking
        console.log('🛰️ Using pre-connected external GPS');
      } else {
        // Use phone GPS - this doesn't trigger any permission prompts during workout
        console.log('📱 Using phone GPS for workout tracking');
        gps.usePhoneGPSOnly();
      }
    }
    
    if (!workoutActive && gps.isTracking) {
      gps.stopTracking();
    }
  }, [workoutActive, workoutPaused, pm5Device]);

  // Stroke detection start/stop and the rate / ST feed live in StrokeDetectionProvider.

  // Feed GPS speed to stroke detection for fallback
  useEffect(() => {
    if (gps.speed && !pm5Device) {
      strokeDetection.updateFromGPSSpeed(gps.speed);
    }
  }, [gps.speed, pm5Device]);

  const { mode: appMode, label: appModeLabel } = useAppMode({ pm5Device, boatClass });
  const getCurveSnapshots = useCurveSnapshots({
    sessionActive: workoutActive,
    running: workoutActive && !workoutPaused,
    distance,
    kind: appMode === 3 ? 'erg' : 'boat',
    demo: demoMode,
    strokeRate: Number(strokeRate) || 0,
    pm5Device,
  });
  // Stroke-averaged split (all modes except Erg — PM5 / Mode 3 stay untouched)
  const strokeSplitEnabled = !pm5Device && appMode !== 3;
  const feedDemoSpeed = useStrokeSplit({
    enabled: strokeSplitEnabled,
    sessionActive: workoutActive,
    demo: demoMode,
    gpsPosition: gps.position,
    gpsSpeed: gps.speed,
    onUpdate: (speed, _mode, av3) => {
      if (!pm5Device) {
        setMetersPerSecond(speed);
        if (av3 > 0) setAvgSplit3(av3);
      }
    },
  });
  // Boat modes: split = 500 ÷ the same smoothed speed the speed tile shows.
  useEffect(() => {
    if (strokeSplitEnabled) setCurrentSplit(speedToSplit(smoothedSpeed));
  }, [smoothedSpeed, strokeSplitEnabled, setCurrentSplit]);

  // ── Pieces: a second session running inside the main one (Screen 2) ──
  const pieces = usePieces({
    sessionActive: workoutActive,
    sessionKey: workoutStartTime.current ? new Date(workoutStartTime.current).toISOString() : null,
    elapsed: elapsedTime,
    distance,
    strokes,
    strokeRate: Number(strokeRate) || 0,
    onPieceEnd: (r) => {
      if (r.ended_reason !== 'session_end') {
        audio.playIntervalEnd?.();
        toast.success(`Piece ${r.piece_number} saved · ${r.piece_name}`, { id: 'piece-saved' });
      }
    },
  });
  const [pieceSetupOpen, setPieceSetupOpen] = useState(false);
  const ergMode = !!pm5Device || appMode === 3;
  const strokeWatts = usePm5StrokeWatts({ enabled: ergMode, sessionActive: workoutActive });
  const [confirmStopOpen, setConfirmStopOpen] = useState(false);
  const stopConfirmedRef = useRef(false);

  // Demo Mode stroke count: +1 on each simulated catch (boat) / force curve (erg)
  useEffect(() => {
    if (!demoMode || pm5Device || !workoutActive || workoutPaused) return undefined;
    const onDemoStroke = (e) => {
      if (!e.detail?.demo || (e.type === 'crewz-accel-catch' && !(e.detail.strokeMs > 0))) return;
      if (!pm5Device) setStrokes((prev) => (prev || 0) + 1);
    };
    window.addEventListener('crewz-accel-catch', onDemoStroke);
    window.addEventListener('crewz-force-curve', onDemoStroke);
    return () => {
      window.removeEventListener('crewz-accel-catch', onDemoStroke);
      window.removeEventListener('crewz-force-curve', onDemoStroke);
    };
  }, [demoMode, pm5Device, workoutActive, workoutPaused, setStrokes]);

  // Collect GPS data during workout
  useEffect(() => {
    if (gps.position && workoutActive && !workoutPaused) {
      workoutDataRef.current.gpsData.push({
        timestamp: Date.now(),
        lat: gps.position.lat,
        lng: gps.position.lng,
        speed: gps.speed,
        source: gps.source
      });
      
      // Distance is owned by the session odometer in WorkoutContext (never reset mid-session).

      // Update speed/split from GPS when available and no PM5 (ERG mode uses PM5 split).
      // Non-Erg modes use the stroke-averaged smoother (useStrokeSplit) instead.
      if (!pm5Device && !strokeSplitEnabled && gps.speed && gps.speed > 0.5) {
        // Convert m/s to split (seconds per 500m)
        const splitFromGPS = 500 / gps.speed;
        updateSmoothedSplit(splitFromGPS);
        setMetersPerSecond(gps.speed);
      }
    }
  }, [gps.position, gps.totalDistance, gps.speed, workoutActive, workoutPaused, pm5Device, strokeSplitEnabled]);

  // Elapsed-time tick & EMA speed smoothing live in WorkoutContext now.

  // Update split calculation separately (not tied to timer) - for elapsed time based split
  // ONLY runs when NO PM5 is connected AND no GPS speed available
  useEffect(() => {
    // Skip if PM5 is connected - PM5 provides split directly via pace
    if (pm5Device) return;
    
    if (strokeSplitEnabled && (gps.position || demoMode)) return;
    if (distance > 0 && elapsedTime > 0 && !gps.speed) {
      // Only use elapsed-time based split if no GPS speed available
      const splitSeconds = (elapsedTime / (distance / 500));
      updateSmoothedSplit(splitSeconds);
    }
  }, [distance, elapsedTime, gps.speed, pm5Device, strokeSplitEnabled, gps.position, demoMode]);

  // ── DEMO-MODE SIMULATOR (Mode 1/2 ONLY) ─────────────────────────────
  // PM5-AUTHORITATIVE FENCE: this whole effect is gated on !pm5Device.
  // The interval below writes to seven metric setters that PM5 also
  // owns (HR / stroke rate / speed / split / distance / watts / cal /
  // stroke count). Never race the PM5 pipeline — if a PM5 is connected
  // this effect must not schedule the interval and any live interval
  // must be torn down. If you're adding a new writer inside this
  // block, keep it inside this !pm5Device gate. See regression fence
  // /app/backend/tests/test_mode3_single_source_of_truth.py.
  useEffect(() => {
    if (demoMode && !pm5Device) {
      if (!workoutActive) {
        setWorkoutActive(true);
        setWorkoutPaused(false);
        workoutStartTime.current = new Date();
        setElapsedTime(0);
        setDistance(0);
        setCalories(0);
        checkFactorDataRef.current = []; // Reset check factor data
        setCheckFactorData([]);
      }
      
      // Demo interval for metrics (runs every 100ms for smoother check factor graph)
      demoHrInterval.current = setInterval(() => {
        const now = Date.now();
        const baseHr = 150;
        const variation = Math.sin(now / 5000) * 30;
        const simulatedHr = Math.floor(baseHr + variation);
        setHeartRate(simulatedHr);
        
        // Stroke rate cycles through half-step values (22, 22.5, 23, 23.5 …)
        // so the new ½ display feature is exercised in demo runs. NK / RP3
        // / SpeedCoach all report half precision — this matches that.
        const rawStrokeRate = 20 + Math.random() * 10;
        const currentStrokeRate = Math.round(rawStrokeRate * 2) / 2;
        setStrokeRate(currentStrokeRate);
        
        // Simulate boat speed with realistic check pattern
        // Speed oscillates with each stroke - higher during drive, lower during recovery
        const strokePeriod = 60000 / (currentStrokeRate || 24); // ms per stroke
        const strokePhase = (now % strokePeriod) / strokePeriod; // 0-1 through stroke cycle
        
        // Simulate drive (0-0.4) and recovery (0.4-1) phases
        let speedVariation;
        if (strokePhase < 0.4) {
          // Drive phase - speed increasing
          speedVariation = Math.sin(strokePhase * Math.PI / 0.4) * 0.8;
        } else {
          // Recovery phase - speed decreasing (the "check")
          const recoveryPhase = (strokePhase - 0.4) / 0.6;
          speedVariation = Math.cos(recoveryPhase * Math.PI * 0.5) * 0.5 - 0.3;
        }
        
        const baseSpeed = demoBoost ? 6.3 : 4.5; // Boost ≈1.4× to cross top-tier WBTs
        const currentSpeed = Math.max(0.5, baseSpeed + speedVariation + (Math.random() * 0.2 - 0.1));
        
        // Calculate split from speed (split = 500m / speed in m/s)
        const instantSplit = currentSpeed > 0 ? 500 / currentSpeed : 0;
        
        // Track split history and calculate 3-stroke average
        // Detect new stroke at phase transition (when phase goes from high to low)
        const lastPhase = splitHistoryRef.current.lastPhase || 0;
        if (strokePhase < 0.1 && lastPhase > 0.9) {
          // New stroke detected - record the average split for this stroke
          const strokeAvgSpeed = checkFactorDataRef.current.length > 0
            ? checkFactorDataRef.current.slice(-20).reduce((sum, d) => sum + d.speed, 0) / Math.min(20, checkFactorDataRef.current.length)
            : currentSpeed;
          const strokeSplit = strokeAvgSpeed > 0 ? 500 / strokeAvgSpeed : 0;
          
          splitHistoryRef.current.splits = splitHistoryRef.current.splits || [];
          splitHistoryRef.current.splits.push(strokeSplit);
          
          // Keep only last 3 splits
          if (splitHistoryRef.current.splits.length > 3) {
            splitHistoryRef.current.splits = splitHistoryRef.current.splits.slice(-3);
          }
          
          // Calculate average of last 3 splits
          if (splitHistoryRef.current.splits.length > 0) {
            const avgSplit = splitHistoryRef.current.splits.reduce((a, b) => a + b, 0) / splitHistoryRef.current.splits.length;
            setAvgSplit3(avgSplit);
          }
        }
        splitHistoryRef.current.lastPhase = strokePhase;
        
        // Update check factor data (keep last 100 readings for ~3 strokes)
        const newDataPoint = {
          timestamp: now,
          speed: currentSpeed,
          strokePhase: strokePhase
        };
        
        checkFactorDataRef.current.push(newDataPoint);
        if (checkFactorDataRef.current.length > 100) {
          checkFactorDataRef.current = checkFactorDataRef.current.slice(-100);
        }
        setCheckFactorData([...checkFactorDataRef.current]);
        
        if (strokeSplitEnabled) {
          // Stroke-averaged split: the smoother owns speed/split updates
          feedDemoSpeed(currentSpeed);
        } else {
        setMetersPerSecond(currentSpeed);

        // Publish the instantaneous split directly into the smoothing
        // buffer so the SPLIT/500M hero updates every 100 ms in demo
        // mode. (The GPS/PM5 pipelines already do this; before this fix
        // demo mode was leaning on the distance/elapsedTime fallback
        // effect which only fires when those two state values change,
        // and can produce a stale/blank split on first render.)
        updateSmoothedSplit(instantSplit);
        }

        setDistance(prev => {
          const newDistance = prev + (currentSpeed * 0.1); // 100ms interval
          return newDistance;
        });
        setWatts(Math.floor(200 + Math.random() * 50));
        // Calories come from the Keytel ticker (simulated HR) — no fake demo increment.
      }, 100); // Update every 100ms for smooth graph
    } else {
      if (demoHrInterval.current) {
        clearInterval(demoHrInterval.current);
      }
    }
    return () => {
      if (demoHrInterval.current) clearInterval(demoHrInterval.current);
    };
  }, [demoMode, workoutActive, pm5Device, demoBoost, strokeSplitEnabled, feedDemoSpeed]);

  // Handle target distance selection with countdown
  const handleTargetDistanceSelect = (dist) => {
    setTargetDistance(dist);
    // Set workout name based on selected distance
    const distName = dist >= 1000 ? `${dist / 1000}K` : `${dist}m`;
    const isRace = targetMode === 'race';
    setWorkoutName(isRace ? `${distName} Race` : `${distName} Target`);

    // Only start a sequence if no workout is already active
    if (workoutActive) return;

    // Race mode is now a TWO-STEP flow:
    //   1. Pick a race distance → just record it (no arm yet).
    //   2. Press the big green START button → arm motion + show READY TO RACE.
    // Standard mode retains the 10-second countdown.
    if (!isRace) {
      startCountdown();
    }
  };

  // Time-target selection. Mirrors handleTargetDistanceSelect: sets the
  // workout name and kicks the 10-second countdown when the rower isn't
  // already mid-workout. Auto-end at target duration is handled by an
  // effect further down.
  const handleTargetDurationSelect = (min) => {
    setTargetDurationMin(min);
    setWorkoutName(`${min}min Target`);
    if (workoutActive) return;
    startCountdown();
  };

  // Time-based target auto-end. When targetMode === 'time' and the
  // elapsed clock crosses the selected minute count, trigger the same
  // stopWorkout() flow the user would get from tapping the button.
  // Guarded by a ref so we don't fire on the same threshold twice, and
  // does nothing in Standard/Race mode so distance targets keep their
  // existing "informational only" behaviour.
  const timeAutoStopFiredRef = useRef(false);
  useEffect(() => {
    if (targetMode !== 'time' || !workoutActive || workoutPaused) return;
    if (timeAutoStopFiredRef.current) return;
    if (elapsedTime >= targetDurationMin * 60) {
      timeAutoStopFiredRef.current = true;
      // Defer to next tick so state settles before teardown.
      window.setTimeout(() => { if (typeof stopWorkout === 'function') stopWorkout(); }, 200);
    }
  }, [elapsedTime, targetMode, workoutActive, workoutPaused, targetDurationMin]);
  // Reset the guard whenever a fresh workout begins.
  useEffect(() => {
    if (!workoutActive) timeAutoStopFiredRef.current = false;
  }, [workoutActive]);

  // ── Stationary watchdog (Brief D) ────────────────────────────────────────
  // When GPS speed reads below the floor for 5 continuous seconds, zero out
  // the live pace, m/s, and SPM display tiles so the monitor matches the
  // behaviour of dedicated rowing hardware (NK SpeedCoach). The cumulative
  // distance accumulator is already frozen inside useGPS at the source so
  // session totals stay accurate. PM5-driven sessions are exempt — the PM5
  // is the authoritative source and reports its own zeroing.
  const STATIONARY_TIMEOUT_MS = 5000;
  const STATIONARY_SPEED_FLOOR = 0.3; // m/s — mirror useGPS floor
  const lastMovingTsRef = useRef(Date.now());
  useEffect(() => {
    if (!workoutActive || workoutPaused || pm5Device) return;
    // Non-Erg: judge motion on the stroke-smoothed speed (GPS speed may be
    // null on some phones, and Demo has no GPS at all).
    const speed = strokeSplitEnabled
      ? (metersPerSecond || 0)
      : (typeof gps.speed === 'number' ? gps.speed : 0);
    if (speed >= STATIONARY_SPEED_FLOOR) {
      lastMovingTsRef.current = Date.now();
      return;
    }
    // Speed below floor — schedule a zeroing pass after the timeout.
    const timer = setTimeout(() => {
      const stillStationary = (Date.now() - lastMovingTsRef.current) >= STATIONARY_TIMEOUT_MS;
      if (stillStationary) {
        setMetersPerSecond(0);
        setCurrentSplit(0);
        setStrokeRate(0);
        smoothedSplitRef.current = [];
      }
    }, STATIONARY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [gps.speed, workoutActive, workoutPaused, pm5Device, setMetersPerSecond, setCurrentSplit, setStrokeRate, strokeSplitEnabled, metersPerSecond]);

  // In Race mode, arm motion detection and show the READY TO RACE overlay
  // instead of starting the workout immediately.
  const startTapTimestampsRef = useRef([]);
  const pendingStartTimerRef = useRef(null);
  const performResetActions = useCallback(() => {
    try { gps.stopTracking && gps.stopTracking(); } catch { /* ignore */ }
    resetWorkout();
    try { gps.resetDistance && gps.resetDistance(); } catch { /* ignore */ }
    smoothedSplitRef.current = [];
    strokeHistoryRef.current = [];
    checkFactorDataRef.current = [];
    splitHistoryRef.current = { splits: [], lastPhase: 0 };
    speedHistoryRef.current = [];
    backgroundElapsedRef.current = 0;
    lastMovingTsRef.current = Date.now();
    toast.success('Monitor reset — all metrics cleared');
  }, [gps, resetWorkout]);

  // Double-tap Start (when no workout is active) fires a reset instead. To
  // make this work reliably we DEFER the actual start by ~350 ms so a second
  // tap within the window can cancel the pending start and route to reset.
  // Once workoutActive is true the wrapper short-circuits — no double-tap
  // gymnastics mid-row.
  const handleStartButtonWithDoubleTap = useCallback(() => {
    if (workoutActive) {
      // Live session: pass through immediately (no debounce, no reset chance)
      handleStartButton();
      return;
    }
    const now = Date.now();
    const ts = startTapTimestampsRef.current;
    ts.push(now);
    while (ts.length && (now - ts[0]) > 400) ts.shift();

    if (ts.length >= 2) {
      // Second tap within 400 ms → cancel the pending start and run reset
      if (pendingStartTimerRef.current) {
        clearTimeout(pendingStartTimerRef.current);
        pendingStartTimerRef.current = null;
      }
      startTapTimestampsRef.current = [];
      performResetActions();
      return;
    }

    // First tap — schedule the actual start after the double-tap window closes
    if (pendingStartTimerRef.current) clearTimeout(pendingStartTimerRef.current);
    pendingStartTimerRef.current = setTimeout(() => {
      pendingStartTimerRef.current = null;
      startTapTimestampsRef.current = [];
      handleStartButton();
    }, 350);
  }, [workoutActive, performResetActions]);

  const handleStartButton = () => {
    if (targetMode === 'race' && targetDistance) {
      race.armRace(() => {
        // Boat lurched (or manual override) — begin the race.
        try { audio.playIntervalStart(); } catch (_e) { /* noop */ }
        try {
          if (isCoxVoiceEnabled() && typeof window !== 'undefined' && 'speechSynthesis' in window) {
            const u = new window.SpeechSynthesisUtterance('Go!');
            u.rate = 1.2;
            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(u);
          }
        } catch (_e) { /* swallow */ }
        startWorkout();
      });
      return;
    }
    startWorkout();
  };

  // Start the 10-second countdown
  const startCountdown = () => {
    setCountdown(10);
    setCountdownFade(false);
    
    // Play initial beep for countdown start
    audio.playCountdownBeep(false);
    
    const countdownInterval = setInterval(() => {
      setCountdownFade(true); // Trigger fade animation
      
      setTimeout(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setCountdownFade(false);
            // Play final beep (longer tone) when countdown finishes
            audio.playCountdownBeep(true);
            startWorkout(); // Start workout after countdown
            return null;
          }
          // Play beep for each countdown number (last 3 seconds get special tone)
          audio.playCountdownBeep(prev <= 4);
          setCountdownFade(false); // Reset fade for next number
          return prev - 1;
        });
      }, 400); // Fade duration
    }, 1000);
  };

  // Cancel countdown
  const cancelCountdown = () => {
    setCountdown(null);
    setCountdownFade(false);
  };

  const startWorkout = () => {
    setCountdown(null); // Clear any countdown
    setScreenLocked(false); // Reset screen lock when starting new workout
    // Use context action to reset all workout metrics + start timer
    ctxStartWorkout(targetDistance);
    // Reset GPS-accumulated distance so the cumulative total runs from this
    // Start to the next Stop (otherwise GPS would carry over its session total).
    try { gps.resetDistance?.(); } catch (_e) { /* noop */ }
    setAvgSplit3(0);
    // Don't reset workoutName here - it was set by target distance selection
    strokeHistoryRef.current = []; // Reset stroke history for M/S calculation
    checkFactorDataRef.current = []; // Reset check factor data
    splitHistoryRef.current = { splits: [], lastPhase: 0 }; // Reset split history
    smoothedSplitRef.current = []; // Reset smoothed split history
    setCheckFactorData([]);
    
    // Reset PM5 metrics if connected (important: clears any stale calorie data)
    if (pm5Device) {
      const pm5 = getPM5Connection();
      if (pm5) {
        pm5.resetMetrics();
        console.log('🚣 PM5 metrics reset for new workout');
      }
    }
    
    // Play start whistle
    audio.playIntervalStart();
    toast.success('Workout started');
  };

  const pauseWorkout = () => {
    if (screenLocked) return; // Don't allow pause when locked
    setWorkoutPaused(!workoutPaused);
    toast.info(workoutPaused ? 'Workout resumed' : 'Workout paused');
  };

  // Long-press to stop - requires holding for 1.5 seconds
  const STOP_HOLD_DURATION = 1500; // ms

  const handleStopTouchStart = () => {
    if (screenLocked) return;
    // Idempotent: if a hold is already in progress, ignore the duplicate call
    // (on mobile both onTouchStart AND a synthesised onMouseDown fire, which
    // previously orphaned the first interval and caused runaway stopWorkout).
    if (stopHoldTimerRef.current) return;
    setStopHoldProgress(0);
    const startTime = Date.now();
    // Capture the interval ID locally so the cleanup branch always targets
    // the correct timer even if another call overwrites the ref.
    const timerId = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / STOP_HOLD_DURATION) * 100, 100);
      setStopHoldProgress(progress);

      if (progress >= 100) {
        clearInterval(timerId);
        if (stopHoldTimerRef.current === timerId) {
          stopHoldTimerRef.current = null;
        }
        setStopHoldProgress(0);
        stopWorkout();
      }
    }, 50);
    stopHoldTimerRef.current = timerId;
  };

  const handleStopTouchEnd = () => {
    if (stopHoldTimerRef.current) {
      clearInterval(stopHoldTimerRef.current);
      stopHoldTimerRef.current = null;
    }
    setStopHoldProgress(0);
  };

  // Long-press to unlock - separate from stop
  const [unlockHoldProgress, setUnlockHoldProgress] = useState(0);
  const unlockHoldTimerRef = useRef(null);

  const handleUnlockTouchStart = () => {
    if (unlockHoldTimerRef.current) return; // idempotent — same fix as stop
    setUnlockHoldProgress(0);
    const startTime = Date.now();
    const timerId = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / STOP_HOLD_DURATION) * 100, 100);
      setUnlockHoldProgress(progress);

      if (progress >= 100) {
        clearInterval(timerId);
        if (unlockHoldTimerRef.current === timerId) {
          unlockHoldTimerRef.current = null;
        }
        setUnlockHoldProgress(0);
        setScreenLocked(false);
        toast.success('Screen unlocked');
      }
    }, 50);
    unlockHoldTimerRef.current = timerId;
  };

  const handleUnlockTouchEnd = () => {
    if (unlockHoldTimerRef.current) {
      clearInterval(unlockHoldTimerRef.current);
      unlockHoldTimerRef.current = null;
    }
    setUnlockHoldProgress(0);
  };

  // Toggle screen lock
  const toggleScreenLock = () => {
    setScreenLocked(!screenLocked);
    toast.info(screenLocked ? 'Screen unlocked' : 'Screen locked - hold unlock button to unlock');
  };

  const stoppingRef = useRef(false);
  const stopWorkout = async () => {
    // Reentrancy guard — protects against double-fire from stacked
    // touch/mouse handlers or any other caller invoking stopWorkout during
    // an in-flight save. Without this a single tap on mobile could trigger
    // the POST /workouts request twice and show duplicate toasts.
    if (stoppingRef.current) {
      console.log('stopWorkout already in progress, ignoring duplicate call');
      return;
    }
    if (!workoutActive) {
      // Workout has already been stopped (likely by a prior invocation from
      // the duplicated touch/mouse event pair). Don't run again.
      return;
    }
    // A piece is running → ask first ("End piece and session?")
    if ((pieces.active || pieces.countdown) && !stopConfirmedRef.current) {
      setConfirmStopOpen(true);
      return;
    }
    stopConfirmedRef.current = false;
    stoppingRef.current = true;
    try {
      // Validate we have a workout to save
      const startTime = workoutStartTime.current;
      const currentElapsed = elapsedTime;
      const currentDistance = distance;

      console.log('stopWorkout called:', {
        startTime,
        elapsedTime: currentElapsed,
        distance: currentDistance,
        workoutActive
      });

      // Don't save if workout never actually started (no start time)
      if (!startTime) {
        setWorkoutActive(false);
        setWorkoutPaused(false);
        toast.info('No workout data to save');
        return;
      }

      // Calculate actual elapsed time in case timer hasn't updated yet
      const actualElapsed = Math.max(
        currentElapsed,
        Math.floor((new Date() - startTime) / 1000)
      );

      // Don't save if workout was less than 1 second
      if (actualElapsed < 1) {
        setWorkoutActive(false);
        setWorkoutPaused(false);
        toast.info('Workout too short to save');
        return;
      }

      // Sanity cap — anything > MAX_WORKOUT_DURATION_SECONDS is almost
      // certainly a stale-session bug (workoutStartTime anchored days ago,
      // browser tab left open, etc.). Drop it instead of polluting history
      // totals with a multi-hour ghost session.
      if (actualElapsed > MAX_WORKOUT_DURATION_SECONDS) {
        console.warn('[stopWorkout] Refusing to save unrealistic duration:', actualElapsed, 's');
        setWorkoutActive(false);
        setWorkoutPaused(false);
        toast.error('Session looks invalid (too long) — discarded');
        return;
      }

      // Play workout complete sound
      audio.playWorkoutComplete();

      setWorkoutActive(false);
      setWorkoutPaused(false);
      setWorkoutCompleted(true);

      // If this was a race that completed (or got close), surface the splits modal.
      // The workout is auto-saved to history via the POST below — modal stays for 5 min
      // (or until dismissed) but the data is durable from this moment.
      if (targetMode === 'race' && raceSplits.length > 0) {
        setShowRaceSplits(true);
      }

      const endTime = new Date();
      const avgSplit = currentDistance > 0 ? (((ergMode && pm5ElapsedTime > 0) ? pm5ElapsedTime : actualElapsed) / (currentDistance / 500)) : 0;
      const boatType = localStorage.getItem('default_boat_type') || 'c2_erg';

      // Determine session name:
      // 1. If PM5 connected -> "Ergo Session"
      // 2. If target distance selected -> "{distance} Target" (e.g., "2K Target")
      // 3. Otherwise -> boat type name
      let sessionName = workoutName;
      if (pm5Device) {
        sessionName = 'Ergo Session';
      } else if (!sessionName) {
        // Default to boat type name if no specific name set
        const boatNames = {
          '1x': 'Single Scull',
          '2x': 'Double Scull',
          '2-': 'Pair',
          '4x': 'Quad',
          '4-': 'Four',
          '4+': 'Coxed Four',
          '8+': 'Eight',
          'c2_erg': 'Erg Session'
        };
        sessionName = boatNames[boatType] || 'Rowing Session';
      }

      console.log('Saving workout:', {
        sessionName,
        boatType,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        duration: actualElapsed,
        distance: Math.round(currentDistance)
      });

      try {
        // CrewZ Session Data Model v1.6 — derive session_type from the
        // current target flow. Compatibility read layer: legacy `duration`
        // and `distance` remain populated so old History rows still work.
        let sessionType = 'standard';
        if (targetMode === 'race') sessionType = 'race';
        else if (targetDistance && !pm5Device) sessionType = 'distance';

        // Snapshot the series buffers before we hand off to the save call.
        const s = workoutDataRef.current;
        const hrSeries = s.hrBpmSeries || [];
        const zoneDist = computeHrZoneDistribution(hrSeries, maxHrInUse(user), restHrInUse(user));
        const hrZoneSeries = buildHrZoneSeries(hrSeries, maxHrInUse(user), restHrInUse(user));
        // avg_cf_pct / avg_rhy_ratio are null in Mode 3 per v1.6 §3.2.
        const avgCfPct = pm5Device ? null : averageSeries(s.cfPctSeries || []);
        const avgRhyRatio = pm5Device ? null : averageSeries(s.rhyRatioSeries || []);

        const payload = {
            session_name: sessionName,
            boat_type: boatType,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            duration: actualElapsed,
            distance: Math.round(currentDistance),
            avg_split: avgSplit,
            avg_stroke_rate: Math.round(strokeRate),
            avg_heart_rate: Math.round(heartRate),
            max_heart_rate: Math.round(heartRate),
            calories: (!ergMode && !hrSeries.length) ? null : Math.round(calculatedCalories),
            avg_watts: Math.round(powerPerRower),
            total_strokes: strokes,
            target_distance: targetDistance,
            heart_rate_data: workoutDataRef.current.heartRateData,
            gps_data: workoutDataRef.current.gpsData,
            stroke_data: workoutDataRef.current.strokeData,
            stroke_arrays: workoutDataRef.current.strokeArrays || [],
            splits_500m: targetMode === 'race' ? raceSplits : null,
            is_pm5_session: !!pm5Device,
            // ── CrewZ Session Data Model v1.6 additions ─────────────────
            // Use ?? (not ||) so a genuinely stationary 0-second moving_time
            // doesn't fall back to elapsed. That would defeat the v1.6 gate
            // (see iter-62 code-review comment).
            moving_time_s: movingTime ?? actualElapsed,
            elapsed_time_s: actualElapsed,
            session_type: sessionType,
            sync_status: 'local',
            avg_cf_pct: avgCfPct,
            avg_rhy_ratio: avgRhyRatio,
            hr_zone_distribution: zoneDist,
            hr_zone_method: HR_ZONE_METHOD,
            speed_ms_series: s.speedSeries || [],
            spm_series: s.spmSeries || [],
            hr_bpm_series: hrSeries,
            hr_zone_series: hrZoneSeries,
            gps_track: workoutDataRef.current.gpsData || [],
            cf_pct_series: s.cfPctSeries || [],
            watts_series: s.wattsSeries || [],
            calories_series: s.caloriesSeries || [],
            rhy_ratio_series: s.rhyRatioSeries || [],
            curve_snapshots: getCurveSnapshots(),
            client_session_id: startTime.toISOString(),
            pieces: pieces.finish('session_end'),
            app_mode: appMode,
            hardware_connected: !!(pm5Device || crewzConnected),
        };
        pieces.clear();
        // Offline-first save: tries POST; on network failure or 5xx queues the
        // workout into localStorage outbox and replays it on the next 'online'
        // event. 4xx (validation errors) are surfaced immediately.
        const result = await saveWorkoutResilient(payload, async (p) => {
          const response = await fetch(`${API}/workouts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(p),
          });
          return response;
        });
        if (result.ok) {
          // Explicit success — server returned 2xx with a saved workout.
          // Include the workout_id + a link to History so the rower can
          // verify it landed instead of trusting an ambiguous toast.
          const savedId = result.body?.workout_id;
          console.info('[stopWorkout] Workout saved:', savedId, 'payload bytes:', JSON.stringify(payload).length);
          toast.success(
            <div className="flex flex-col gap-1">
              <div><strong>Workout saved to history</strong></div>
              {savedId && <div className="text-xs opacity-70">ID: {savedId.slice(-8)}</div>}
              <a
                href="/history"
                className="text-xs underline text-blue-300 hover:text-blue-200"
                data-testid="workout-saved-history-link"
              >
                View in History →
              </a>
            </div>,
            { duration: 6000, id: 'workout-save' }
          );
        } else if (result.queued) {
          // The rower probably conflates "saved locally" with "saved to history".
          // Be explicit that this sits in a local queue until connectivity is
          // restored so they know why it's missing from the History tab.
          console.warn('[stopWorkout] Queued to outbox:', result.error);
          toast.warning('Saved locally — no server response. Will sync when you\'re back online.', { duration: 6000, id: 'workout-save' });
        } else {
          console.error('Save workout error:', result);
          toast.error(`Failed to save workout (${result.status || 'network'})`, { id: 'workout-save' });
        }
      } catch (error) {
        toast.error('Failed to save workout');
        console.error(error);
      }
    } finally {
      stoppingRef.current = false;
    }
  };

  const exportFit = () => {
    if (!user || elapsedTime === 0) {
      toast.error('No workout data to export');
      return;
    }

    try {
      const xml = generateTcxFile({
        startTime: workoutStartTime.current,
        duration: elapsedTime,
        distance: distance,
        heartRateData: workoutDataRef.current.heartRateData,
        gpsData: workoutDataRef.current.gpsData,
        calories: calories,
        avgHeartRate: workoutDataRef.current.heartRateData.length > 0
          ? Math.round(workoutDataRef.current.heartRateData.reduce((s, p) => s + (p.hr || 0), 0) / workoutDataRef.current.heartRateData.length)
          : null,
      });

      const blob = new Blob([xml], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const filename = `crewz-workout-${new Date().toISOString().split('T')[0]}.tcx`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      toast.error(`Failed to export TCX file: ${error?.message || error}`);
      console.error('TCX export error:', error);
    }
  };

  // Main (hero) display always opens on Split /500m; a change lasts for this visit only.
  const [heroMetric, setHeroMetric] = useState('split');
  const handlePreferenceChange = (section, metricId) => {
    if (section === 'mainDisplay') { setHeroMetric(metricId); return; }
    setMetricSlot(section, metricId);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSplit = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    // Remove tenths - just show M:SS format like PM5
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate projected finish time based on current M/S pace and target distance
  const calculateProjectedFinish = () => {
    if (metersPerSecond <= 0 || distance <= 0) {
      return '--:--';
    }
    
    const remainingDistance = Math.max(0, targetDistance - distance);
    const timeToFinish = remainingDistance / metersPerSecond;
    const projectedTotalTime = elapsedTime + timeToFinish;
    
    // Format as MM:SS or H:MM:SS if over an hour
    const totalSeconds = Math.floor(projectedTotalTime);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Use user's max HR, or a default of 185 for demo mode
  const maxHR = maxHrInUse(user);
  const hrZone = heartRate > 0 ? calculateHRZone(heartRate, maxHR, restHrInUse(user)) : null;

  // ── Oarlock smart-gate calibration ─────────────────────────────────────────
  // When CrewZ NK/XBoat smart gates start streaming, the user's saved
  // oarlock config (oar length, inboard, ZF/ZA, MFW2) is applied here. The
  // resulting power overrides the formula-based estimate below. Until then,
  // `oarlock.streaming` is false and the rest of the pipeline is unchanged.
  const oarlockSingleSculling = (() => {
    try {
      // Both vendors share a toggle - use whichever the user has flipped
      return localStorage.getItem('oarlock_single_scull_nk_empower') === 'true'
          || localStorage.getItem('oarlock_single_scull_xboat') === 'true';
    } catch { return false; }
  })();
  const nkOarlock = useOarlockCalibration({ vendor: 'nk_empower', boatType: boatClass, singleSculling: oarlockSingleSculling });
  const xbOarlock = useOarlockCalibration({ vendor: 'xboat', boatType: boatClass, singleSculling: oarlockSingleSculling });
  const oarlock = nkOarlock.streaming ? nkOarlock : (xbOarlock.streaming ? xbOarlock : null);

  // ── Combined per-side gate readings (NK + XBoat) ─────────────────────────
  // Either vendor's gate can occupy a side; merge the two `latestBySide`
  // dicts so the downstream `peak_force_*` / `catch_angle_*` metrics and
  // side-default override logic only need to inspect a single map.
  const combinedLatestBySide = {
    ...(nkOarlock.latestBySide || {}),
    ...(xbOarlock.latestBySide || {}),
  };
  const gateInstalled = {
    port: !!combinedLatestBySide.port,
    starboard: !!combinedLatestBySide.starboard,
    sweep: !!(combinedLatestBySide.sweep || combinedLatestBySide.single_scull),
  };
  // Pick the right "single-side" key for sweep / single-scull rigs.
  const sweepReading = combinedLatestBySide.sweep || combinedLatestBySide.single_scull || null;

  // ── App mode detection + per-mode screen-layout preferences ────────────────
  // Force Session Goal on Mode 3 (Ergo/PM5) ONLY when a PM5 device is
  // actually connected. Selecting the Ergo boat class alone (without a
  // live PM5) still leaves the user in charge of their left-tile pref,
  // so switching the tile off actually hides it. CF% remains meaningless
  // on the erg but the setting is theirs to make.
  // Ergo mode always defaults to Session Goal (user rule 2026-08-31): CF% is
  // GPS-derived and has no meaning on an erg. This overrides the rower's
  // saved preference while Mode 3 is active — every other mode uses their
  // saved value (default 'off' for new accounts).
  const effectiveLeftTile = (appMode === 3) ? 'session_goal' : leftTilePref;
  // Per-mode + per-(targetMode, distance) screen layout. Falls back to mode-only.
  const { preferences: metricPreferences, setSlot: setMetricSlot } = useMetricPreferences(appMode, {
    targetMode,
    targetDistance,
  });

  // Calculate derived metrics
  // For coxswains with 'average' or 'roster' mode, use their crew's average weight
  // Otherwise, use weight category
  const shouldUseCrewWeight = isCoxswain && crewWeightMode !== 'category' && crewAvgWeight && crewAvgWeight > 0;
  const boatMass = calculateBoatMass(boatClass, weightCategory, { 
    isCoxswain: shouldUseCrewWeight, 
    crewAvgWeight: shouldUseCrewWeight ? crewAvgWeight : null 
  });
  const crewCount = CREW_COUNTS[boatClass] || 1;
  
  // Power calculation precedence:
  //   1. CrewZ smart-gate (live calibrated reading from oarlocks)
  //   2. PM5 (when connected) - already in watts
  //   3. Formula from boat speed × boat mass
  const oarlockBoatPower = oarlock ? oarlock.aggregate.power_w : 0;
  const calculatedPower = oarlockBoatPower > 0
    ? oarlockBoatPower
    : (ergMode ? strokeWatts : calculatePowerFromSpeed(metersPerSecond, boatMass));
  const powerPerRowerRaw = crewCount > 1 ? Math.round(calculatedPower / crewCount) : calculatedPower;

  // ── EMA smoothing on Power (α = 0.3) ────────────────────────────────────
  // Per-stroke power readings (esp. from the NK gate when only one side is
  // streaming) can jump between adjacent strokes by 200–400 W. The user
  // explicitly requested an exponential moving average smoother — applied to
  // POWER ONLY so per-stroke force/angle metrics remain stroke-accurate for
  // dual-gate validation.
  //
  //   emaValue = α × newValue + (1 − α) × previousEmaValue
  //
  // Reset to 0 between sessions; recover quickly on resume because α is high.
  const powerEmaRef = useRef(0);
  useEffect(() => {
    if (!workoutActive) { powerEmaRef.current = 0; return; }
    if (!Number.isFinite(powerPerRowerRaw) || powerPerRowerRaw <= 0) return;
    const alpha = 0.3;
    const prev = powerEmaRef.current;
    powerEmaRef.current = prev > 0
      ? alpha * powerPerRowerRaw + (1 - alpha) * prev
      : powerPerRowerRaw;
  }, [powerPerRowerRaw, workoutActive]);
  // Erg: PM5 last-stroke power (CE060036) exactly as received — no smoothing.
  const powerPerRower = ergMode && oarlockBoatPower <= 0
    ? strokeWatts
    : workoutActive && powerEmaRef.current > 0
    ? Math.round(powerEmaRef.current)
    : powerPerRowerRaw;
  
  // Power inputs for the stroke sensor log (on-water estimate only).
  const powerEstimated = !ergMode && oarlockBoatPower <= 0;
  useEffect(() => {
    setLogPowerSample(powerEstimated
      ? { speed: metersPerSecond, mass: boatMass, pBoat: calculatedPower, pwrShown: powerPerRower }
      : null);
  }, [powerEstimated, metersPerSecond, boatMass, calculatedPower, powerPerRower]);
  useEffect(() => () => setLogPowerSample(null), []);

  // Track peak power
  useEffect(() => {
    if (workoutActive && powerPerRower > peakPower) {
      setPeakPower(powerPerRower);
    }
  }, [workoutActive, powerPerRower, peakPower]);

  // ── Phone-mode cumulative calorie ticker (Keytel formula) ─────────────────
  // Runs every 10 s while the workout is active, not paused, and NO PM5 is
  // attached. Each tick adds kcal/min ÷ 6 to a fractional accumulator (kept
  // in a ref so React doesn't re-render on every tenth-second) and commits
  // only the integer kcal delta to the shared `calories` state. Net result:
  // CAL pill updates ~once every 25–30 s in phone mode instead of waiting a
  // full minute, but cumulative totals stay identical to the 60-s variant.
  //
  // HR is read via `keytelHrRef` so the interval doesn't restart each beat.
  const keytelHrRef = useRef(0);
  useEffect(() => { keytelHrRef.current = heartRate; }, [heartRate]);
  const keytelFractionalKcalRef = useRef(0);
  useEffect(() => {
    if (!workoutActive || workoutPaused || pm5Device) return undefined;
    const ageYears = ageFromDob(user?.dob);
    const weightKg = user?.weight;
    const sex = user?.sex;
    // Skip altogether if we don't have the profile data Keytel needs — no
    // calorie ticks beats fabricated ones.
    if (!ageYears || !weightKg || !sex) return undefined;

    // Reset the fractional accumulator at the start of each tick session so
    // pause/resume doesn't carry stale fractions across long gaps.
    keytelFractionalKcalRef.current = 0;

    const TICK_MS = 10_000;
    const TICKS_PER_MIN = 60_000 / TICK_MS; // 6
    const tick = () => {
      const kcalPerMin = keytelKcalPerMinute({
        heartRate: keytelHrRef.current,
        weightKg,
        ageYears,
        sex,
      });
      if (kcalPerMin <= 0) return;
      keytelFractionalKcalRef.current += kcalPerMin / TICKS_PER_MIN;
      // Commit whole kcal to state; keep the fractional remainder for next tick.
      const whole = Math.floor(keytelFractionalKcalRef.current);
      if (whole >= 1) {
        keytelFractionalKcalRef.current -= whole;
        setCalories(prev => (prev || 0) + whole);
      }
    };
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [workoutActive, workoutPaused, pm5Device, user?.dob, user?.weight, user?.sex, setCalories]);

  // Cumulative calories — single source of truth (`calories` from
  // WorkoutContext). Sources by mode:
  //   • Mode 3 (Ergo): PM5 0x0032 reports cumulative kcal directly (cal totals
  //     are pushed by `handlePM5ExtraData` → setCalories).
  //   • Mode 2 (Phone): the Keytel-formula 60 s ticker below adds kcal/min
  //     into the same cumulative state.
  //   • Mode 1 (Hardware): not yet implemented — falls through to whatever
  //     was last set; will be wired when CrewZ firmware exposes a metric.
  const calculatedCalories = calories;
  livePowerRef.current = { v: powerPerRower || 0, t: Date.now() };
  // Check Factor: demo trace, else live on-water trace (CF is null in Erg mode).
  const liveCfData = useCheckFactorFeed({ enabled: workoutActive && !demoMode && !ergMode, baseSpeed: metersPerSecond });
  const cfData = demoMode ? checkFactorData : liveCfData;
  const cfValue = ergMode ? null : computeCheckFactor(cfData)?.cf;
  
  // Average split (overall pace)
  // Avg Split: on Mode 3 we use the PM5's own time-under-load clock so the
  // number matches what the C2 monitor is showing (its avg split excludes
  // rest intervals). Every other mode uses the session ELAP wall-clock.
  const _avgSplitElapsed = pm5Device ? pm5ElapsedTime : elapsedTime;
  const avgSplit = calculateAverageSplit(distance, _avgSplitElapsed);
  
  // Distance per stroke
  const distancePerStroke = calculateDistancePerStroke(distance, strokes);
  
  // Work per stroke (Joules)
  const workPerStroke = calculateWorkPerStroke(powerPerRower, strokeRate);
  
  // Estimated stroke length
  const strokeLength = estimateStrokeLength(distancePerStroke);

  // Calculate projected distance at 30:00 — blended current-vs-average pace.
  //
  // Why blended? An instantaneous-only projection (1800/split × 500) follows
  // a late sprint and overshoots; a pure-average projection lags pace changes
  // early. We weight `currentPace` more at the start (responsive) and `avgPace`
  // more at the finish (converges to actual). At t=30:00 the weight reaches
  // 1.0 so P30 equals the real distance covered.
  //
  // Sources are mode-aware automatically because `elapsedTime` + `distance`
  // already reflect:
  //   • Mode 3 (Ergo): PM5 char 0x0032 elapsed + 0x0031 distance (authoritative).
  //   • Mode 2 (Phone): wall-clock elapsed + accumulated GPS distance.
  //   • Mode 1 (Full hardware): CrewZ-reported elapsed + distance.
  // ── P30 — Projected distance at 30:00 ────────────────────────────────────
  //
  // Algorithm: cumulative-first blended projection.
  //
  //   P30 = distance_covered + projected_for_remaining_time
  //
  // Remaining time is projected using a weighted blend of:
  //   • currentPace (instantaneous — responsive to pace changes)
  //   • avgPace     (session average — stable, converges to actual)
  //
  // Weight shifts linearly from 0 → 1 over the 30-minute window:
  //   • t=0:00  weight=0.00 → 100% current pace  (all we have)
  //   • t=15:00 weight=0.50 → 50/50 blend
  //   • t=29:00 weight=0.97 → 97% average         (nearly actual)
  //   • t=30:00 weight=1.00 → returns covered     (exact actual)
  //
  // Data sources are mode-aware via WorkoutContext:
  //   Mode 3 (Ergo) → elapsedTime + distance driven by PM5 0x0031/0x0032
  //   Mode 2 (Phone) → wall-clock moving time + GPS accumulated distance
  //   Mode 1 (Hardware) → CrewZ BLE elapsed + distance
  //
  // Edge cases:
  //   • Session not started / distance=0 → return 0 (show '--')
  //   • Distance < 50 m (first few strokes) → suppress average; use current
  //     pace only to avoid a wildly inflated avgPace from slow early strokes.
  //   • Session overrun (elapsed > 30:00) → return actual distance covered.
  //   • All pace values invalid → return covered (no projection).
  const calculateProjected30min = () => {
    const TARGET_SECONDS = 1800;
    const elapsed = elapsedTime;   // seconds — PM5-authoritative in Mode 3
    const covered = distance;      // metres  — PM5-authoritative in Mode 3

    // Not enough data yet
    if (elapsed <= 0 || covered <= 0) return 0;

    const remaining = TARGET_SECONDS - elapsed;

    // Session complete or overrun — show actual distance
    if (remaining <= 0) return Math.round(covered);

    // Session-average pace (sec/500m).
    // Suppress if distance is trivially small — e.g. the first 2–3 strokes
    // (< 50m) can produce a very slow average that skews early projections.
    const avgPaceValid = covered >= 50;
    const avgPace = avgPaceValid ? (elapsed / covered) * 500 : null;

    // Current pace from PM5 / GPS split — valid range 0:30–10:00 / 500m
    const currentPaceValid = currentSplit > 0 && currentSplit < 600;

    // Determine the pace to use for the remaining-distance projection
    let blendedPace;
    if (!currentPaceValid && !avgPaceValid) {
      // No valid pace at all — nothing to project
      return Math.round(covered);
    } else if (!avgPaceValid) {
      // Too early for a meaningful session average — use current pace only
      blendedPace = currentSplit;
    } else if (!currentPaceValid) {
      // Current pace invalid (idle / paused) — fall back to average
      blendedPace = avgPace;
    } else {
      // Both valid — blend linearly.
      // weight = 0 at start (favour current), 1 at 30:00 (favour average).
      const weight = Math.min(elapsed / TARGET_SECONDS, 1);
      blendedPace = (currentSplit * (1 - weight)) + (avgPace * weight);
    }

    if (!blendedPace || blendedPace <= 0) return Math.round(covered);

    const projectedRemaining = (remaining / blendedPace) * 500;
    return Math.round(covered + projectedRemaining);
  };

  // ── Stationary speed gate ─────────────────────────────────────────────────
  // When the boat / ergo is effectively stationary, the Speed pill goes to
  // '—'. The MAIN Split/500m display falls back to '0:00' instead (per UX
  // request — feels more "ready to go" on the pre-workout screen than a
  // dash). Threshold 0.3 m/s ≈ 27:46/500m — anything slower is noise from
  // stationary GPS drift or a not-yet-recovered handle. Applied to ALL modes;
  // PM5 already zeroes its outputs, but Mode 2 (phone GPS) and Mode 5
  // (XPod-only) benefit immediately. Avg-Split tiles keep the dash so the
  // rower can still distinguish "no data yet" from "live 0:00".
  const MIN_MOVING_SPEED_MPS = 0.3;
  const isMoving = (smoothedSpeed || 0) >= MIN_MOVING_SPEED_MPS;
  // Non-Erg: stroke-averaged split as M:SS.s, --:-- below 0.5 m/s. Erg unchanged.
  const splitDisplay = strokeSplitEnabled
    ? (smoothedSpeed >= MIN_SPLIT_SPEED ? formatSplitTenths(currentSplit) : '--:--')
    : (isMoving && currentSplit > 0 && currentSplit < 600 ? formatSplit(currentSplit) : '0:00');
  // Erg: speed = 500 ÷ PM5 split (PM5 split stays the truth). Boat: smoothed speed.
  const ergSplitValid = ergMode && currentSplit > 0 && currentSplit < 600;
  const speedDisplay = ergMode
    ? (ergSplitValid && isMoving ? (500 / currentSplit).toFixed(2) : '—')
    : (isMoving ? smoothedSpeed.toFixed(2) : '—');

  // ── %WBT — percentage of World Best Time speed for this profile+boat ──
  // On-water: (currentSpeed / targetSpeed) × 100 (higher = closer to WBT).
  // Ergo:     (targetSplit / currentSplit) × 100 (inverted since lower split wins).
  // Silent state ('—') when no target exists for this profile+boat combo
  // OR when the rower is stationary — mirrors the crown's silence rule.
  const _wbtMode = appMode === 3 ? 'ergo' : 'on_water';
  const { target: _wbtTarget } = useWBTTarget(_wbtMode, boatClass);
  const wbtPercent = (() => {
    if (!_wbtTarget || !Number.isFinite(_wbtTarget)) return '—';
    if (_wbtMode === 'ergo') {
      if (!currentSplit || currentSplit <= 0) return '—';
      return `${((_wbtTarget / currentSplit) * 100).toFixed(1)}%`;
    }
    if (!isMoving || !Number.isFinite(smoothedSpeed) || smoothedSpeed <= 0) return '—';
    return `${((smoothedSpeed / _wbtTarget) * 100).toFixed(1)}%`;
  })();

  // All 15 metrics
  const allMetrics = {
    // 1. Split / 500m  (zero-velocity gate: shows '—' while stationary)
    split: splitDisplay,
    // 2. Stroke Rate
    stroke_rate: strokeRate > 0 || ergMode ? strokeRate : '--',
    // 3. Distance
    distance: Math.floor(distance),
    // 4. Speed / Pace (m/s) - zero-velocity gated; smoothed
    meters_per_second: speedDisplay,
    // 4b. Speed in km/h — same underlying GPS-derived value, different
    // unit conversion. Exposed as its own metric so the World Rowing
    // side-by-side layout (m/s + km/h shown together) is possible via
    // the standard slot picker. `speedDisplay` may be a formatted
    // string ('—' when stationary) so guard the numeric conversion.
    speed_kmh: (() => {
      const n = typeof speedDisplay === 'number' ? speedDisplay : parseFloat(speedDisplay);
      return Number.isFinite(n) ? (n * 3.6).toFixed(1) : '—';
    })(),
    // 4c. %WBT — percentage of World Best Time speed/pace for the current
    // profile+boat category. Renders '—' when no matching WBT record
    // exists (silent state, mirrors the crown).
    wbt_percent: wbtPercent,
    // 5. Heart Rate
    heart_rate: heartRate,
    // 6. Power (per crew member)
    watts: powerPerRower,
    power: powerPerRower,
    power_estimated: powerEstimated,
    // 7. Stroke Length (estimated)
    stroke_length: strokeLength.toFixed(2),
    // 8. Check Factor
    check_factor: cfValue != null ? cfValue.toFixed(1) : '—',
    // 9. Time / Elapsed — session wall-clock from Start to Stop.
    time: formatTime(elapsedTime),
    // 9b. PM5 Time — raw time-under-load from the C2 monitor. Only ticks
    // while a PM5 is streaming (Mode 3). Silent '—' otherwise.
    pm5_time: pm5Device ? formatTime(pm5ElapsedTime) : '—',
    // 10. Calories
    calories: Math.round(calculatedCalories),
    // 11. Average Split
    avg_split: avgSplit > 0 && avgSplit < 600 ? formatSplit(avgSplit) : '—',
    // 12. Peak Power
    peak_power: peakPower,
    // 13. Stroke Count
    strokes: strokes,
    // 14. Distance per Stroke
    distance_per_stroke: distancePerStroke.toFixed(1),
    // 15. Work per Stroke (Joules)
    work_per_stroke: workPerStroke,
    // Additional useful metrics
    zone: hrZone ? hrZone.label : 'N/A',
    projected_finish: calculateProjectedFinish(),
    projected_30min: calculateProjected30min(),
    target_distance: targetDistance,
    avg_split_3: avgSplit3 > 0 && avgSplit3 < 600 ? formatSplit(avgSplit3) : '—',
    rhythm_ratio: formatRhythmRatio(rhythmRatio),
    // Raw numeric ratio is also exposed so the metric tile can colour-code
    // the value based on the rhythm zone (rose / amber / sky / emerald / zinc).
    rhythm_ratio_raw: rhythmRatio,
    // ── Power-gate per-side metrics (NK EmPower / XBoat) ──────────────────
    // Peak force is reported in Newtons; catch angle in degrees. Values are
    // pulled from the latest stroke summary per side and only update while
    // the corresponding gate is streaming.
    peak_force_port: combinedLatestBySide.port ? Math.round(combinedLatestBySide.port.force_n || 0) : 0,
    peak_force_starboard: combinedLatestBySide.starboard ? Math.round(combinedLatestBySide.starboard.force_n || 0) : 0,
    peak_force_sweep: sweepReading ? Math.round(sweepReading.force_n || 0) : 0,
    catch_angle_port: combinedLatestBySide.port ? (combinedLatestBySide.port.angle_deg || 0).toFixed(1) : '0.0',
    catch_angle_starboard: combinedLatestBySide.starboard ? (combinedLatestBySide.starboard.angle_deg || 0).toFixed(1) : '0.0',
    catch_angle_sweep: sweepReading ? (sweepReading.angle_deg || 0).toFixed(1) : '0.0',
    // Acceleration / force curve graph: erg (Mode 3) shows PM5 force curve
    is_erg: appMode === 3
  };

  // ── Side-aware default metric overrides ──────────────────────────────────
  // When a power gate is installed on a given side, default that side's
  // metric tiles to Peak Force (top) + Catch Angle (bottom). Sweep / single-
  // scull rigs only have one gate, so when present we mirror the reading on
  // both sides. Saved user preferences (passed in via `metricPreferences`)
  // always take precedence — these are only the *fallback* defaults.
  const leftTopDefault = gateInstalled.port
    ? 'peak_force_port'
    : (gateInstalled.sweep ? 'peak_force_sweep' : 'time');
  const leftBottomDefault = gateInstalled.port
    ? 'catch_angle_port'
    : (gateInstalled.sweep ? 'catch_angle_sweep' : 'strokes');
  const rightTopDefault = gateInstalled.starboard
    ? 'peak_force_starboard'
    : (gateInstalled.sweep ? 'peak_force_sweep' : 'calories');
  const rightBottomDefault = gateInstalled.starboard
    ? 'catch_angle_starboard'
    : (gateInstalled.sweep ? 'catch_angle_sweep' : 'distance');

  return (
    <div 
      className="min-h-screen flex flex-col bg-white dark:bg-zinc-900 transition-colors"
      onClick={handleScreenTouch}
      onTouchStart={handleScreenTouch}
    >
      {/* TopNav - hidden during active workout after 10s */}
      <div className={`transition-all duration-300 ${workoutActive && !controlsVisible ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}>
        <TopNav workoutActive={workoutActive} onEndSession={stopWorkout} />
      </div>

      <RowingDetectedLight />
      {/* Main Content - Optimized for landscape mode when controls hidden */}
      <div className={`flex-1 flex flex-col ${workoutActive && !controlsVisible ? 'p-2 landscape:p-3' : 'p-4'}`}>
        <div className={`max-w-7xl mx-auto w-full flex-1 flex flex-col ${workoutActive && !controlsVisible ? 'justify-center' : ''}`} data-testid="workout-monitor">
          {/* New cockpit hero — replaces the old mode badge + 3 top tiles +
              main split + 4 side metrics block. Slot picker / long-press all
              still wired via <ClickableMetric>. Edge glow lights up only
              when a HR strap is connected and colours per the AES zone. */}
          <SwipeScreens enabled={pieces.hasScreen2} screen={pieces.screen} onScreen={pieces.setScreen}>
          <div className={pieces.hasScreen2 ? 'relative pt-12' : 'relative'}>
          {pieces.hasScreen2 && <ScreenBadge screen={1} label="Session" />}
          <CockpitDisplay
            darkMode={darkMode}
            cockpitMode={isFullscreen}
            modeLabel={`Mode ${appMode} · ${appModeLabel}`}
            heartRate={heartRate}
            restingHR={restHrInUse(user)}
            maxHR={maxHR}
            strapConnected={heartRate > 0}
            metricPreferences={{ ...metricPreferences, mainDisplay: heroMetric }}
            allMetrics={allMetrics}
            metricDefaults={{
              top1: 'meters_per_second',
              top2: 'heart_rate',
              top3: 'stroke_rate',
              sideLeft1: leftTopDefault,
              sideLeft2: leftBottomDefault,
              sideRight1: rightTopDefault,
              sideRight2: rightBottomDefault,
              mainDisplay: 'split',
            }}
            onPreferenceChange={handlePreferenceChange}
            strokeData={cfData}
            compact={workoutActive && !controlsVisible}
            onSwipeDown={() => { if (isFullscreen) toggleFullscreen(); }}
            paceTargetOn={paceTargetOn}
            paceTargetSplit={paceTargetSplit}
            currentSplitSec={currentSplit}
            boatType={boatClass}
            appMode={appMode}
            tileFontStep={tileFontStep}
            inProgram={false}
            onTogglePaceTarget={() => setPaceTargetOn(v => !v)}
            onChangeTargetSplit={(delta) => setPaceTargetSplit(s => Math.max(60, Math.min(600, s + delta)))}
            showCheckFactor={showCheckFactor}
            checkFactorStrokeData={checkFactorData}
            leftTileDisplay={effectiveLeftTile}
            sessionGoalProps={{
              workoutActive,
              workoutPaused,
              elapsedSec: elapsedTime,
              currentSplitSec: currentSplit,
              strokeRate,
              metersPerSecond,
              goal: sessionGoal,
              onGoalChange: updateSessionGoal,
              onSessionComplete: handleSessionGoalComplete,
            }}
          />
          </div>
          <PieceScreen
            piece={pieces.active}
            progress={pieces.progress}
            countdown={pieces.countdown}
            summary={pieces.summary}
            currentSplit={(strokeSplitEnabled ? smoothedSpeed : metersPerSecond) >= 0.5 ? currentSplit : 0}
            strokeRate={strokeRate}
            onEnd={() => pieces.finish('manual')}
            onDismissSummary={pieces.dismissSummary}
            onCancelCountdown={pieces.cancelCountdown}
          />
          </SwipeScreens>

          <PieceSetupDialog open={pieceSetupOpen} onOpenChange={setPieceSetupOpen} onStart={(def, mode) => pieces.start(def, mode)} />
          <AlertDialog open={confirmStopOpen} onOpenChange={setConfirmStopOpen}>
            <AlertDialogContent className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 border border-zinc-200 dark:border-zinc-700" data-testid="end-piece-and-session-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>End piece and session?</AlertDialogTitle>
                <AlertDialogDescription className="text-zinc-600 dark:text-zinc-300">The running piece is saved, then the whole session is saved.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-200" data-testid="end-piece-and-session-cancel">Keep rowing</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-[#1565C0] hover:bg-[#0D47A1] text-white"
                  data-testid="end-piece-and-session-confirm"
                  onClick={() => { stopConfirmedRef.current = true; setConfirmStopOpen(false); stopWorkout(); }}
                >
                  End both
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* When fullscreen-cockpit is active, the sections below are
              hidden — cockpit fills the viewport. */}
          {!isFullscreen && (
            <div className={`${workoutActive && !controlsVisible ? 'space-y-2 landscape:space-y-1' : 'space-y-4'}`}>
              {/* Force balance bar (port vs starboard) — auto-renders only when both gates stream */}
              <ForceBalanceIndicator
                latestBySide={(oarlock?.latestBySide) || nkOarlock.latestBySide || xbOarlock.latestBySide}
              />

              {/* Stroke visualizer — force-vs-angle curve with theoretical CA/FA brackets and slip/wash wedges.
                  Renders only when smart-gate hardware is streaming (or after a stroke was captured). */}
              <StrokeVisualizer hardwareConnected={!!(nkOarlock.streaming || xbOarlock.streaming || (oarlock?.streaming))} />

              {/* Session CSV Logger — on-water validation tool.
                  Independent of workout state; samples once per second and
                  exports a CSV download to the phone. */}
              <SessionCsvLogger
                getRow={() => ({
                  elapsedTime,
                  distance,
                  pace: currentSplit > 0 && currentSplit < 600 ? formatSplit(currentSplit) : '',
                  spm: strokeRate || 0,
                  lat: gps.position?.lat ?? null,
                  lng: gps.position?.lng ?? null,
                  accuracy: gps.accuracy ?? null,
                })}
              />
            </div>
          )}

          {/* Energy System Zone - Small indicator with rounded edges */}
          <div className={`flex justify-center items-center gap-3 ${workoutActive && !controlsVisible ? 'mt-1 landscape:mt-2' : 'mt-3'}`}>
            {/* Accelerometer Status Indicator (when no PM5) */}
            {!pm5Device && workoutActive && (
              <div 
                className={`px-3 py-1.5 font-bold tracking-wide text-xs rounded-lg shadow-sm flex items-center gap-1.5 ${
                  strokeDetection.debugInfo?.method === 'stationary'
                    ? 'bg-zinc-400 text-white'
                    : strokeDetection.debugInfo?.method !== 'none' && strokeRate > 0
                    ? 'bg-purple-500 text-white' 
                    : 'bg-amber-500 text-white'
                }`}
                title={`Method: ${strokeDetection.debugInfo?.method}, AC: ${strokeDetection.debugInfo?.autocorrRate || 0}, ZC: ${strokeDetection.debugInfo?.zcRate || 0}`}
              >
                <Activity className="w-3 h-3" />
                {strokeDetection.debugInfo?.method === 'stationary' 
                  ? '📱 Stationary'
                  : strokeRate > 0
                  ? `📱 ${strokeRate} spm (${strokeDetection.debugInfo?.method || ''})`
                  : `📱 Detecting...`}
              </div>
            )}
            {/* GPS Status Indicator */}
            {gps.hasGPS && (
              <div 
                className={`px-3 py-1.5 font-bold tracking-wide text-xs rounded-lg shadow-sm flex items-center gap-1.5 ${
                  gps.isPhoneGPS 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-green-500 text-white'
                }`}
              >
                <MapPin className="w-3 h-3" />
                {gps.isPhoneGPS ? 'Phone GPS' : 'CREWZ GPS'}
                {gps.accuracy && <span className="opacity-75">±{Math.round(gps.accuracy)}m</span>}
              </div>
            )}
            {!gps.hasGPS && workoutActive && (
              <div className="px-3 py-1.5 bg-zinc-300 dark:bg-zinc-600 text-zinc-600 dark:text-zinc-300 font-bold tracking-wide text-xs rounded-lg shadow-sm flex items-center gap-1.5">
                <MapPin className="w-3 h-3" />
                GPS Off
              </div>
            )}
            {/* HR Zone */}
            {hrZone && (
              <div 
                className="px-4 py-1.5 text-white font-bold tracking-wide text-sm rounded-lg shadow-sm"
                style={{ 
                  backgroundColor: hrZone.color
                }}
              >
                {hrZone.zone}
              </div>
            )}
          </div>

          {/* Target Distance Selector - Hidden during active workout */}
          <div className={`mt-4 transition-all duration-300 ${workoutActive && !controlsVisible ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}>
            {/* Standard / Race / Time mode toggle */}
            <div className="flex justify-center items-center gap-2 mb-2">
              <span className="text-xs font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Target:</span>
              <div className="inline-flex items-center bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5" data-testid="target-mode-toggle">
                <button
                  type="button"
                  onClick={() => setTargetMode('standard')}
                  data-testid="target-mode-standard"
                  className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
                    targetMode === 'standard'
                      ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm'
                      : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                  }`}
                >
                  Standard
                </button>
                <button
                  type="button"
                  onClick={() => setTargetMode('race')}
                  data-testid="target-mode-race"
                  className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
                    targetMode === 'race'
                      ? 'bg-rose-600 text-white shadow-sm'
                      : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                  }`}
                >
                  Race
                </button>
                <button
                  type="button"
                  onClick={() => setTargetMode('time')}
                  data-testid="target-mode-time"
                  className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
                    targetMode === 'time'
                      ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm'
                      : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                  }`}
                >
                  Time
                </button>
              </div>
            </div>
            {/* Value row — swaps between distance and time buttons based on
                targetMode. Time mode is completely independent of the
                Session Goal duration stepper on the left tile. */}
            {targetMode === 'time' ? (
              <div className="flex justify-center items-center gap-2 flex-wrap" data-testid="target-time-row">
                {[5, 10, 15, 20, 30, 40, 50, 60, 70, 80, 90].map((min) => (
                  <button
                    key={min}
                    onClick={() => handleTargetDurationSelect(min)}
                    data-testid={`target-time-${min}`}
                    className={`px-3 py-1.5 text-sm font-bold rounded-lg transition-all ${
                      targetDurationMin === min
                        ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                    }`}
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {min}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex justify-center items-center gap-2 flex-wrap">
                {(targetMode === 'race'
                  ? [500, 1000, 2000, 5000]
                  : [500, 1000, 2000, 5000, 10000, 15000, 20000, 25000, 30000]
                ).map((dist) => (
                  <button
                    key={dist}
                    onClick={() => handleTargetDistanceSelect(dist)}
                    data-testid={`${targetMode === 'race' ? 'race' : 'target'}-${dist}`}
                    className={`px-3 py-1.5 text-sm font-bold rounded-lg transition-all ${
                      targetDistance === dist
                        ? targetMode === 'race'
                          ? 'bg-rose-600 text-white'
                          : 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                    }`}
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {dist >= 1000 ? `${dist / 1000}K` : dist}
                  </button>
                ))}
              </div>
            )}
            {targetMode === 'race' && !workoutActive && (
              <p className="text-center text-[11px] text-zinc-500 dark:text-zinc-400 mt-2 px-3" data-testid="race-hint">
                {targetDistance
                  ? 'Race distance set. Tap the pink START button when you\'re ready to arm the boat-lurch trigger.'
                  : 'Pick a race distance above, then tap the big START button to enter READY TO RACE.'}
              </p>
            )}
            {targetMode === 'time' && !workoutActive && (
              <p className="text-center text-[11px] text-zinc-500 dark:text-zinc-400 mt-2 px-3" data-testid="time-hint">
                Pick a duration above — the workout will auto-end at {targetDurationMin} minutes.
              </p>
            )}
          </div>

          {/* Countdown Overlay */}
          {countdown !== null && (
            <div 
              className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
              onClick={cancelCountdown}
            >
              <div className="text-center">
                <div 
                  className={`text-[20rem] font-black text-white transition-all duration-400 ${
                    countdownFade 
                      ? 'opacity-0 scale-150 blur-sm' 
                      : 'opacity-90 scale-100 blur-0'
                  }`}
                  style={{ 
                    fontFamily: 'JetBrains Mono, monospace',
                    textShadow: '0 0 100px rgba(255,255,255,0.5), 0 0 200px rgba(255,255,255,0.3)',
                    lineHeight: 1
                  }}
                >
                  {countdown}
                </div>
                <div className={`text-2xl font-bold text-white/60 mt-4 uppercase tracking-widest transition-opacity duration-300 ${
                  countdownFade ? 'opacity-0' : 'opacity-100'
                }`}>
                  {targetDistance >= 1000 ? `${targetDistance / 1000}K` : `${targetDistance}m`} workout
                </div>
                <div className="text-sm text-white/40 mt-8">
                  Tap anywhere to cancel
                </div>
              </div>
            </div>
          )}

          {/* Race READY overlay — shown while armed, replaced by workout when motion fires */}
          {race.armed && !workoutActive && (
            <div
              className="fixed inset-0 bg-black/85 flex items-center justify-center z-50"
              data-testid="race-ready-overlay"
            >
              <div className="text-center px-6 max-w-md">
                <div
                  className="text-[6rem] sm:text-[8rem] font-black text-rose-500 leading-[0.9] animate-pulse"
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    textShadow: '0 0 100px rgba(244,63,94,0.6), 0 0 200px rgba(244,63,94,0.3)',
                  }}
                  data-testid="race-ready-headline"
                >
                  READY<br/>TO RACE
                </div>
                <div className="mt-4 text-xl font-bold text-white uppercase tracking-widest">
                  {targetDistance >= 1000 ? `${targetDistance / 1000}K` : `${targetDistance}m`} race
                </div>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  Sit in the blocks. The timer starts the instant the boat surges —
                  small movements are ignored.
                </p>
                <div className="mt-5 flex flex-col gap-2 items-center">
                  <div className="text-[11px] text-white/50 uppercase tracking-wider">
                    Motion {race.magnitude.toFixed(1)} m/s² · trigger {race.threshold.toFixed(1)}
                  </div>
                  <div className="w-48 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-rose-500 transition-[width] duration-100"
                      style={{ width: `${Math.min(100, (race.magnitude / race.threshold) * 100)}%` }}
                      data-testid="race-motion-meter"
                    />
                  </div>
                </div>
                {race.permissionNeeded && (
                  <p className="mt-3 text-[11px] text-amber-300">
                    Motion access denied. Use Manual Start or grant motion permission in your browser.
                  </p>
                )}
                <div className="mt-6 flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => race.cancelArm()}
                    className="px-4 py-2 rounded-lg bg-white/10 text-white text-xs font-bold uppercase tracking-wider hover:bg-white/20 transition"
                    data-testid="race-cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => race.manualLaunch()}
                    className="px-4 py-2 rounded-lg bg-rose-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-rose-700 transition"
                    data-testid="race-manual-start-button"
                  >
                    Manual Start
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Race Splits Modal — auto-saved to History on workout stop; modal lingers
              up to 5 min so the rower can review before dismissing manually. */}
          {showRaceSplits && raceSplits.length > 0 && (
            <RaceSplitsCard
              splits={raceSplits}
              raceDistance={targetDistance}
              variant="modal"
              autoDismissMs={5 * 60 * 1000}
              context={{
                date: new Date().toISOString(),
                isErgo: !!pm5Device,
                boatClass: localStorage.getItem('default_boat_type') || '1x',
                savedToHistory: true,
              }}
              onClose={() => { setShowRaceSplits(false); resetRaceSplits(); }}
            />
          )}

          {/* Screen Lock Overlay */}
          {screenLocked && workoutActive && (
            <div className="fixed inset-0 z-40 pointer-events-none">
              {/* Lock indicator at top */}
              <div className="absolute top-20 left-1/2 -translate-x-1/2 pointer-events-auto">
                <button
                  onTouchStart={(e) => { e.stopPropagation(); handleUnlockTouchStart(); }}
                  onTouchEnd={(e) => { e.stopPropagation(); handleUnlockTouchEnd(); }}
                  onMouseDown={(e) => { e.stopPropagation(); handleUnlockTouchStart(); }}
                  onMouseUp={(e) => { e.stopPropagation(); handleUnlockTouchEnd(); }}
                  onMouseLeave={handleUnlockTouchEnd}
                  className="relative flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-full shadow-lg overflow-hidden"
                  data-testid="unlock-button"
                >
                  {/* Progress bar */}
                  {unlockHoldProgress > 0 && (
                    <div 
                      className="absolute left-0 top-0 h-full bg-blue-300 transition-all duration-100"
                      style={{ width: `${unlockHoldProgress}%` }}
                    />
                  )}
                  <Lock className="w-4 h-4 relative z-10" />
                  <span className="text-sm font-bold relative z-10">
                    {unlockHoldProgress > 0 ? 'Unlocking...' : 'Hold to Unlock'}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Control Buttons - Animated Oval Style - Hidden during active workout after 10s */}
          <div className={`mt-6 flex gap-4 justify-center items-center transition-all duration-300 ${workoutActive && !controlsVisible ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}>
            {/* Reset Button — to the LEFT of Volume Down. Clears any lingering
                session state (the ghost session that sometimes lingers after
                a fresh login) AND zeros every live metric. One tap, no confirm
                (window.confirm is silently blocked inside the Emergent preview
                iframe — the action is non-destructive anyway). */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                // Stop GPS tracking so no new samples sneak in mid-reset
                try { gps.stopTracking && gps.stopTracking(); } catch { /* ignore */ }
                // Reset all context state + localStorage
                resetWorkout();
                // Reset GPS accumulator AND last-position anchor
                try { gps.resetDistance && gps.resetDistance(); } catch { /* ignore */ }
                // Reset every WorkoutMonitorV2-local smoothing buffer so old
                // GPS jitter can't seed the next session
                smoothedSplitRef.current = [];
                strokeHistoryRef.current = [];
                checkFactorDataRef.current = [];
                splitHistoryRef.current = { splits: [], lastPhase: 0 };
                speedHistoryRef.current = [];
                backgroundElapsedRef.current = 0;
                lastMovingTsRef.current = Date.now();
                toast.success('Monitor reset — all metrics cleared');
              }}
              data-testid="reset-button"
              className="group relative w-14 h-14 rounded-full transition-all duration-300 hover:scale-110 active:scale-95"
              title="Reset monitor (does not save)"
            >
              <div className="absolute inset-0 rounded-full border-3 border-amber-400 bg-gradient-to-b from-amber-50 to-amber-100 shadow-md group-hover:shadow-lg transition-shadow" />
              <div className="absolute inset-1.5 rounded-full bg-gradient-to-b from-white to-amber-50 opacity-80" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <RotateCcw className="w-5 h-5 text-amber-600 group-hover:scale-110 transition-transform" />
                <span className="text-[8px] font-bold text-amber-500 mt-0.5">RESET</span>
              </div>
            </button>

            {/* Font Size Down (A−) — shrinks the top + side tile numbers one
                step toward default. Same footprint as the volume/reset dials. */}
            <button
              onClick={(e) => { e.stopPropagation(); bumpTileFont(-1); resetHideTimer(); }}
              disabled={tileFontStep <= 0}
              data-testid="font-size-down-button"
              className={`group relative w-14 h-14 rounded-full transition-all duration-300 ${tileFontStep <= 0 ? 'opacity-40 cursor-not-allowed' : 'hover:scale-110 active:scale-95'}`}
              title="Decrease tile number size"
              aria-label="Decrease tile number size"
            >
              <div className="absolute inset-0 rounded-full border-3 border-zinc-400 bg-gradient-to-b from-gray-100 to-gray-200 shadow-md group-hover:shadow-lg transition-shadow" />
              <div className="absolute inset-1.5 rounded-full bg-gradient-to-b from-white to-gray-100 opacity-80" />
              <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
                <span className="text-zinc-600 font-black tracking-tight"><span className="text-xs align-top">A</span><span className="text-base">−</span></span>
                <span className="text-[8px] font-bold text-zinc-400 mt-0.5">SIZE</span>
              </div>
            </button>

            {/* Font Size Up (A+) — enlarges the top + side tile numbers one
                step, capped at 4 presses so the cockpit layout stays intact. */}
            <button
              onClick={(e) => { e.stopPropagation(); bumpTileFont(1); resetHideTimer(); }}
              disabled={tileFontStep >= TILE_FONT_STEP_MAX}
              data-testid="font-size-up-button"
              className={`group relative w-14 h-14 rounded-full transition-all duration-300 ${tileFontStep >= TILE_FONT_STEP_MAX ? 'opacity-40 cursor-not-allowed' : 'hover:scale-110 active:scale-95'}`}
              title="Increase tile number size"
              aria-label="Increase tile number size"
            >
              <div className="absolute inset-0 rounded-full border-3 border-zinc-400 bg-gradient-to-b from-gray-100 to-gray-200 shadow-md group-hover:shadow-lg transition-shadow" />
              <div className="absolute inset-1.5 rounded-full bg-gradient-to-b from-white to-gray-100 opacity-80" />
              <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
                <span className="text-zinc-700 font-black tracking-tight"><span className="text-sm align-top">A</span><span className="text-base">+</span></span>
                <span className="text-[8px] font-bold text-zinc-400 mt-0.5">SIZE</span>
              </div>
            </button>

            {/* Volume Down Button - Left side */}
            <button
              onClick={(e) => { 
                e.stopPropagation(); 
                const newVol = Math.max(0, audio.volume - 0.1);
                audio.updateVolume(newVol);
                audio.testSound();
                toast.info(`Volume: ${Math.round(newVol * 100)}%`, { duration: 1000 });
              }}
              data-testid="volume-down-button"
              className="group relative w-14 h-14 rounded-full transition-all duration-300 hover:scale-110 active:scale-95"
              title={`Volume: ${Math.round(audio.volume * 100)}%`}
            >
              <div className="absolute inset-0 rounded-full border-3 border-zinc-400 bg-gradient-to-b from-gray-100 to-gray-200 shadow-md group-hover:shadow-lg transition-shadow" />
              <div className="absolute inset-1.5 rounded-full bg-gradient-to-b from-white to-gray-100 opacity-80" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Volume1 className="w-5 h-5 text-zinc-500 group-hover:scale-110 transition-transform" />
                <span className="text-[8px] font-bold text-zinc-400 mt-0.5">{Math.round(audio.volume * 100)}%</span>
              </div>
            </button>

            {!workoutActive ? (
              <button
                onClick={handleStartButtonWithDoubleTap}
                data-testid="start-button"
                className={`group relative w-24 h-24 rounded-full transition-all duration-300 hover:scale-110 active:scale-95 ${targetMode === 'race' && targetDistance ? 'ring-4 ring-rose-500/40 ring-offset-2' : ''}`}
              >
                {/* Outer ring */}
                <div className={`absolute inset-0 rounded-full border-4 ${targetMode === 'race' && targetDistance ? 'border-rose-500' : 'border-green-500'} bg-gradient-to-b from-gray-100 to-gray-200 shadow-lg group-hover:shadow-xl transition-shadow`} />
                {/* Inner glow animation */}
                <div className="absolute inset-2 rounded-full bg-gradient-to-b from-white to-gray-100 opacity-80" />
                {/* Icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Play className={`w-10 h-10 ${targetMode === 'race' && targetDistance ? 'text-rose-500 fill-rose-500' : 'text-green-500 fill-green-500'} ml-1 group-hover:scale-110 transition-transform`} />
                </div>
                {/* Pulse animation on hover */}
                <div className={`absolute inset-0 rounded-full border-4 ${targetMode === 'race' && targetDistance ? 'border-rose-400' : 'border-green-400'} opacity-0 group-hover:opacity-100 group-hover:animate-ping`} />
              </button>
            ) : (
              <>
                {/* Screen Lock Button */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleScreenLock(); resetHideTimer(); }}
                  data-testid="lock-button"
                  className={`group relative w-14 h-14 rounded-full transition-all duration-300 hover:scale-110 active:scale-95 ${screenLocked ? 'ring-2 ring-blue-400' : ''}`}
                >
                  <div className={`absolute inset-0 rounded-full border-3 ${screenLocked ? 'border-blue-500 bg-blue-100' : 'border-zinc-400 bg-gradient-to-b from-gray-100 to-gray-200'} shadow-md group-hover:shadow-lg transition-shadow`} />
                  <div className="absolute inset-1.5 rounded-full bg-gradient-to-b from-white to-gray-100 opacity-80" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {screenLocked ? (
                      <Lock className="w-5 h-5 text-blue-500 group-hover:scale-110 transition-transform" />
                    ) : (
                      <Unlock className="w-5 h-5 text-zinc-500 group-hover:scale-110 transition-transform" />
                    )}
                  </div>
                </button>

                {/* Piece Button — start a piece / jump to Screen 2 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (screenLocked) return;
                    if (pieces.hasScreen2) pieces.setScreen(2); else setPieceSetupOpen(true);
                    resetHideTimer();
                  }}
                  data-testid="piece-button"
                  className={`group relative w-14 h-14 rounded-full transition-all duration-300 ${screenLocked ? 'opacity-40 cursor-not-allowed' : 'hover:scale-110 active:scale-95'}`}
                >
                  <div className={`absolute inset-0 rounded-full border-3 ${pieces.active ? 'border-[#1565C0] bg-blue-100' : 'border-[#1565C0]/70 bg-gradient-to-b from-gray-100 to-gray-200'} shadow-md group-hover:shadow-lg transition-shadow`} />
                  <div className="absolute inset-1.5 rounded-full bg-gradient-to-b from-white to-gray-100 opacity-80" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Flag className="w-5 h-5 text-[#1565C0] group-hover:scale-110 transition-transform" />
                    <span className="text-[8px] font-bold text-[#1565C0] mt-0.5">{pieces.active ? 'PIECE ●' : 'PIECE'}</span>
                  </div>
                </button>

                <SessionLogButton disabled={screenLocked} />

                {/* Pause/Resume Button - Red (disabled when locked) */}
                <button
                  onClick={(e) => { e.stopPropagation(); if (!screenLocked) { pauseWorkout(); resetHideTimer(); } }}
                  data-testid="pause-button"
                  className={`group relative w-24 h-24 rounded-full transition-all duration-300 ${screenLocked ? 'opacity-40 cursor-not-allowed' : 'hover:scale-110 active:scale-95'}`}
                >
                  <div className="absolute inset-0 rounded-full border-4 border-red-500 bg-gradient-to-b from-gray-100 to-gray-200 shadow-lg group-hover:shadow-xl transition-shadow" />
                  <div className="absolute inset-2 rounded-full bg-gradient-to-b from-white to-gray-100 opacity-80" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    {workoutPaused ? (
                      <Play className="w-10 h-10 text-red-500 fill-red-500 ml-1 group-hover:scale-110 transition-transform" />
                    ) : (
                      <Pause className="w-10 h-10 text-red-500 fill-red-500 group-hover:scale-110 transition-transform" />
                    )}
                  </div>
                  {!screenLocked && <div className="absolute inset-0 rounded-full border-4 border-red-400 opacity-0 group-hover:opacity-100 group-hover:animate-ping" />}
                </button>

                {/* Stop Button - Yellow/Orange - Long Press Required (disabled when locked) */}
                <button
                  onTouchStart={(e) => { e.stopPropagation(); if (!screenLocked) handleStopTouchStart(); }}
                  onTouchEnd={(e) => { e.stopPropagation(); handleStopTouchEnd(); }}
                  onMouseDown={(e) => { e.stopPropagation(); if (!screenLocked) handleStopTouchStart(); }}
                  onMouseUp={(e) => { e.stopPropagation(); handleStopTouchEnd(); }}
                  onMouseLeave={handleStopTouchEnd}
                  data-testid="stop-button"
                  className={`group relative w-24 h-24 rounded-full transition-all duration-300 ${screenLocked ? 'opacity-40 cursor-not-allowed' : 'hover:scale-110 active:scale-95'}`}
                >
                  <div className="absolute inset-0 rounded-full border-4 border-amber-500 bg-gradient-to-b from-gray-100 to-gray-200 shadow-lg group-hover:shadow-xl transition-shadow" />
                  <div className="absolute inset-2 rounded-full bg-gradient-to-b from-white to-gray-100 opacity-80" />
                  {/* Progress ring for long-press */}
                  {stopHoldProgress > 0 && (
                    <svg className="absolute inset-0 w-full h-full -rotate-90">
                      <circle
                        cx="50%"
                        cy="50%"
                        r="46"
                        fill="none"
                        stroke="#f59e0b"
                        strokeWidth="4"
                        strokeDasharray={`${(stopHoldProgress / 100) * 289} 289`}
                        className="transition-all duration-100"
                      />
                    </svg>
                  )}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Square className="w-8 h-8 text-amber-500 fill-amber-500 group-hover:scale-110 transition-transform" />
                    {!screenLocked && stopHoldProgress === 0 && (
                      <span className="text-[8px] text-amber-600 font-bold mt-0.5">HOLD</span>
                    )}
                  </div>
                  {!screenLocked && stopHoldProgress === 0 && <div className="absolute inset-0 rounded-full border-4 border-amber-400 opacity-0 group-hover:opacity-100 group-hover:animate-ping" />}
                </button>
              </>
            )}

            {/* Volume Up Button - Right side */}
            <button
              onClick={(e) => { 
                e.stopPropagation(); 
                const newVol = Math.min(1, audio.volume + 0.1);
                audio.updateVolume(newVol);
                audio.testSound();
                toast.info(`Volume: ${Math.round(newVol * 100)}%`, { duration: 1000 });
              }}
              data-testid="volume-up-button"
              className="group relative w-14 h-14 rounded-full transition-all duration-300 hover:scale-110 active:scale-95"
              title={`Volume: ${Math.round(audio.volume * 100)}%`}
            >
              <div className="absolute inset-0 rounded-full border-3 border-zinc-400 bg-gradient-to-b from-gray-100 to-gray-200 shadow-md group-hover:shadow-lg transition-shadow" />
              <div className="absolute inset-1.5 rounded-full bg-gradient-to-b from-white to-gray-100 opacity-80" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Volume2 className="w-5 h-5 text-zinc-500 group-hover:scale-110 transition-transform" />
                <span className="text-[8px] font-bold text-zinc-400 mt-0.5">{Math.round(audio.volume * 100)}%</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* ── Floating Mode Heartbeat + Fullscreen + NK Debug Buttons ────── */}
      {/* Offset below the TopNav so the Settings tab stays tappable. */}
      <div className="fixed top-20 right-3 z-[70] flex items-center gap-2">
        {appMode === 5 && (
          <div
            data-testid="xpod-mode-pulse"
            title={
              nkFreshness === 'live'
                ? 'MODE 5 · XBoat XPod — data flowing'
                : nkFreshness === 'stale'
                  ? 'MODE 5 · XBoat XPod — no recent frames'
                  : 'MODE 5 · XBoat XPod — paired but silent'
            }
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border-2 shadow-lg ${
              nkFreshness === 'live'
                ? 'bg-emerald-500 border-emerald-300 text-white'
                : nkFreshness === 'stale'
                  ? 'bg-amber-500 border-amber-300 text-white'
                  : 'bg-zinc-400 border-zinc-300 text-white'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full bg-white ${nkFreshness === 'live' ? 'animate-pulse' : ''}`}
            />
            MODE 5 · XPOD
          </div>
        )}
        <button
          onClick={toggleNkDebug}
          data-testid="nk-debug-toggle"
          className={`w-10 h-10 rounded-full border-2 shadow-lg flex items-center justify-center transition-colors ${
            nkDebugOpen
              ? 'bg-emerald-500 border-emerald-300 text-white'
              : 'bg-white/90 dark:bg-zinc-800/90 border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-200'
          }`}
          title="NK EmPower debug panel"
          aria-label="Toggle NK EmPower debug panel"
        >
          <Bug className="w-4 h-4" />
        </button>
        <button
          onClick={toggleFullscreen}
          data-testid="fullscreen-toggle"
          className="w-10 h-10 rounded-full border-2 border-zinc-300 dark:border-zinc-600 bg-white/90 dark:bg-zinc-800/90 shadow-lg flex items-center justify-center text-zinc-700 dark:text-zinc-200"
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>

      <OarlockDebugPanel open={nkDebugOpen} onClose={() => { setNkDebugOpen(false); try { localStorage.setItem('crewz_nk_debug_panel', '0'); } catch { /* noop */ } }} />
    </div>
  );
}

